import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useGetRequest from '../../../api/useGetRequest';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faCheck,
  faLayerGroup,
  faMap,
  faRoute,
  faRoad,
  faWeight,
  faBox,
  faEuroSign,
  faLeaf,
  faExclamationTriangle,
  faSearch,
  faEraser,
  faGripLines,
  faIndustry,
  faCog,
  faTrash,
  faMagic,
  faMagicWandSparkles,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import L from 'leaflet';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  Polyline,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import axios from 'axios';

import GenericPageContainer from '../../../components/agv-generic-page-container';
import ModalConfig from '../../FleetManagement/modalManagement/modalConfig';
import CustomDatePickerDashboard from '../../../components/agv-header-date-picker';
import usePostReorderSteps from '../../../api/tours-planning/usePostReorderSteps';
import usePatchUnassignStep from '../../../api/tours-planning/usePatchUnassignStep';
import usePostAssignSteps from '../../../api/tours-planning/usePostAssignSteps';
import useDeleteUnassignedStep from '../../../api/tours-planning/useDeleteUnassignedStep';
import usePatchStepETA from '../../../api/tours-planning/usePatchStepETA';
import './scheduling.css';
import useGeneralConfig from '../../../api/general-configuration/useGeneralConfig';
import useOptimizeVehicleSequence from '../../../api/tours-planning/useOptimizeVehicleSequence';
import useOptimizeUnassignedSteps from '../../../api/tours-planning/useOptimizeUnassignedSteps';
import { useSelector } from 'react-redux';

moment.locale('fr');

// Configuration pour les clusters de marqueurs
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let className = 'marker-cluster-small';

  if (count >= 10) {
    size = 'medium';
    className = 'marker-cluster-medium';
  }
  if (count >= 100) {
    size = 'large';
    className = 'marker-cluster-large';
  }

  return L.divIcon({
    html: `
      <div style="
        background: linear-gradient(135deg, #079992 0%,rgb(10, 179, 170) 100%);
        border: 3px solid white;
        border-radius: 50%;
        width: ${size === 'small' ? '40px' : size === 'medium' ? '50px' : '60px'};
        height: ${size === 'small' ? '40px' : size === 'medium' ? '50px' : '60px'};
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size === 'small' ? '14px' : size === 'medium' ? '16px' : '18px'};
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        animation: pulse 2s infinite;
      ">
        ${count}
      </div>
    `,
    className: `marker-cluster ${className}`,
    iconSize: [
      size === 'small' ? 40 : size === 'medium' ? 50 : 60,
      size === 'small' ? 40 : size === 'medium' ? 50 : 60,
    ],
    iconAnchor: [
      size === 'small' ? 20 : size === 'medium' ? 25 : 30,
      size === 'small' ? 20 : size === 'medium' ? 25 : 30,
    ],
  });
};

export default function Scheduling() {
  // États pour les données importées
  const [trucks, setTrucks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  // États pour les filtres
  const [filters, setFilters] = useState({
    searchQuery: '',
    selectedTruckFilters: [], // Changé en tableau pour sélection multiple
    selectedDate: moment().format('YYYY-MM-DD'), // Date par défaut
  });

  // Construction de l'URL pour l'API avec les paramètres de filtrage
  const buildApiUrl = React.useCallback(() => {
    const params = new URLSearchParams();

    // Date de base (obligatoire)
    params.append('tour_date', filters.selectedDate);

    // Filtre de recherche (nom correct: 'query')
    if (filters.searchQuery.trim()) {
      params.append('query', filters.searchQuery.trim());
    }

    // Filtres par camions (nom correct: 'vehicle_keys')
    if (filters.selectedTruckFilters.length > 0) {
      // Convertir les IDs des camions en clés (vehicle_keys)
      const vehicleKeys = filters.selectedTruckFilters
        .map((truckId) => {
          const truck = trucks.find((t) => t.id === truckId);
          return truck?.key || truck?.id; // Utiliser key si disponible, sinon id
        })
        .filter(Boolean);

      if (vehicleKeys.length > 0) {
        params.append('vehicle_keys', vehicleKeys.join(','));
      }
    }

    return `/tours-planning/scheduled?${params.toString()}`;
  }, [filters, trucks]);

  const scheduledToursUrl = buildApiUrl();

  const config = useSelector((state) => state.config);


  // Hook pour récupérer les données planifiées
  const {
    data: scheduledData,
    isLoading: isLoadingScheduled,
    isFirstLoad,
    error: scheduledError,
    mutate: mutateScheduledData,
  } = useGetRequest(scheduledToursUrl, 300);

  // Hook pour réorganiser les étapes
  const {
    reorderSteps,
    isLoading: reorderLoading,
    isSuccess: reorderSuccess,
  } = usePostReorderSteps();

  // Hook pour désaffecter une étape
  const {
    unassignStep,
    isLoading: unassignLoading,
    isSuccess: unassignSuccess,
  } = usePatchUnassignStep();

  // Hook pour affecter des étapes
  const {
    assignSteps,
    isLoading: assignLoading,
    isSuccess: assignSuccess,
  } = usePostAssignSteps();

  // Hook pour supprimer une étape non affectée
  const {
    deleteUnassignedStep,
    isLoading: deleteLoading,
    isSuccess: deleteSuccess,
  } = useDeleteUnassignedStep();

  // Hook pour mettre à jour l'ETA d'une étape
  const {
    updateStepETA,
    isLoading: etaUpdateLoading,
    isSuccess: etaUpdateSuccess,
  } = usePatchStepETA();

  // États pour la modale d'assignation unifiée
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);

  // État pour la carte
  const [truckColors, setTruckColors] = useState({});

  // Créer une référence à la carte pour y accéder directement
  const mapRef = React.useRef(null);
  const [selectedMapTruck, setSelectedMapTruck] = useState(null);
  const [deliveryCoordinates, setDeliveryCoordinates] = useState({});
  const [vehicleOptimizationStats, setVehicleOptimizationStats] = useState({});
  const [routeErrorDeliveries, setRouteErrorDeliveries] = useState({});

  // États pour les données de routage
  const [routeData, setRouteData] = useState({});
  const [fetchingRoutes, setFetchingRoutes] = useState(false);
  const [isFullOptimizationRunning, setIsFullOptimizationRunning] = useState(false);

  // Hook pour la configuration générale
  const {
    generalConfig,
    isLoading: isLoadingGeneralConfig,
    error: generalConfigError,
    fetchGeneralConfig: loadGeneralConfig,
    refreshConfig: refreshGeneralConfig,
    isConfigValid,
  } = useGeneralConfig();

  // Hook pour l'optimisation des véhicules
  const {
    optimizeVehicleSequence: optimizeSequence,
    isVehicleOptimizing,
    isLoading: isOptimizationLoading,
    error: optimizationError,
    data: optimizationData,
  } = useOptimizeVehicleSequence();
  const {
    optimizeAllSteps,
    isLoading: isGlobalOptimizationLoading,
  } = useOptimizeUnassignedSteps();

  // KPI calculés
  const [kpiData, setKpiData] = useState({
    totalKilometers: 0,
    totalWeight: 0,
    totalTrucks: 0,
    totalDeliveries: 0,
    totalCost: 0,
    totalCO2: 0,
    co2PerDelivery: 0,
    totalMissions: 0,
    unassignedMissions: 0,
    missionsWithoutETA: 0,
  });
  const [factories, setFactories] = useState([]);
  const [factoryCoordinates, setFactoryCoordinates] = useState(null);

  // États pour le drag and drop
  const [draggedDelivery, setDraggedDelivery] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // État pour gérer l'affichage de la modale de configuration
  const [showModalConfig, setShowModalConfig] = useState(false);

  // La configuration générale est maintenant gérée par le hook useGeneralConfig

  // Effet pour traiter les données récupérées par l'API
  useEffect(() => {
    if (scheduledData) {
      processData(scheduledData);
    }
  }, [scheduledData]);

  useEffect(() => {
    loadGeneralConfig();
  }, [showModalConfig]);

  // Effet pour gérer les erreurs
  useEffect(() => {
    if (scheduledError) {
      console.error('Erreur lors du chargement des données:', scheduledError);
      toast.error('Erreur lors du chargement des données planifiées');
    }
  }, [scheduledError]);

  const processData = (res) => {
    // Réinitialiser les sélections de livraisons lors du rechargement des données
    setSelectedDeliveries([]);

    // Extraire les factories si elles sont présentes dans la réponse
    if (res && res.factories && res.factories.length > 0) {
      setFactories(res.factories);

      // Prendre les coordonnées de la première factory comme point de départ/fin par défaut
      const factory = res.factories[0];
      if (factory && factory.latitude && factory.longitude) {
        setFactoryCoordinates({
          lat: parseFloat(factory.latitude),
          lng: parseFloat(factory.longitude),
        });
      }
    }

    // Utiliser les véhicules provenant de la réponse API
    let vehiclesArray = [];
    if (res && res.all_vehicles) {
      vehiclesArray = res.all_vehicles.map((vehicle) => ({
        id: vehicle.vehicule_id,
        key: vehicle.vehicule_key,
        licensePlate: vehicle.imat || 'Non spécifié',
        type: vehicle.vehicle_type || 'Non spécifié',
        capacity:
          typeof vehicle.total_capacity === 'number'
            ? vehicle.total_capacity
            : parseFloat(vehicle.total_capacity) || 1800,
        usedCapacity: 0, // Cette valeur sera mise à jour côté front pour l'instant
        linearCapacity:
          typeof vehicle.linear_capacity === 'number'
            ? vehicle.linear_capacity
            : parseFloat(vehicle.linear_capacity) || 0,
        usedLinearMeters: 0, // Cette valeur sera mise à jour après avoir chargé les étapes
        color: vehicle.color || '#cccccc',
      }));
    }

    // Utiliser les étapes de livraison de la réponse API
    let deliveriesArray = [];
    if (res && res.steps) {
      // Trier les steps par order avant de les traiter
      const sortedSteps = res.steps.sort((a, b) => {
        const orderA = a.step_linked_if_any?.order || 0;
        const orderB = b.step_linked_if_any?.order || 0;
        return orderA - orderB;
      });

      deliveriesArray = sortedSteps.map((step, index) => {
        // Extraire les données de l'adresse
        const endAddress = step.raw_data?.delivery_detail?.step?.end_address;
        const stepLinked = step.step_linked_if_any;
        const tourLinked = step.tour_linked_if_any;

        // Nom du client (pas d'ETA pour l'instant)
        const clientName = endAddress?.name || 'N/A';
        const clientDisplay = clientName;

        // Calculer le nombre de produits à partir des items
        const productCount = step.raw_data?.delivery_detail?.items?.length || 0;

        return {
          id: stepLinked?.step_id || `step_${index + 1}`,
          client: clientDisplay,
          clientName: clientName, // Garder le nom original séparément
          etaTime:
            step?.step_linked_if_any?.estimated_delivery &&
            moment(step.step_linked_if_any.estimated_delivery).format(
              'HH:mm'
            ) !== '00:00'
              ? moment(step.step_linked_if_any.estimated_delivery).format(
                  'HH:mm'
                )
              : null, // Récupérer seulement l'heure de l'ETA sauvegardée
          street: endAddress?.street || 'N/A',
          zipcode: endAddress?.zipcode || 'N/A',
          city: endAddress?.city || 'N/A',
          address: formatAddress(
            endAddress?.street,
            endAddress?.zipcode,
            endAddress?.city
          ),
          products: `${productCount} produit(s)`,
          weight: parseFloat((step.total_weight || 0).toFixed(2)),
          linearMeters: 0, // Pas de mètres linéaires pour l'instant
          date: formatDate(step.delivery_date) || 'N/A',
          truck: tourLinked?.vehicle?.imat || 'Non affecté',
          vehicleId: tourLinked?.vehicle?.vehicule_id || null,
          tourId: tourLinked?.tour_id || null,
          order: stepLinked?.order || 0,
          tourPlanningId: step.id, // ID du tour planning pour l'API
          coordinates:
            endAddress?.latitude !== undefined &&
            endAddress?.longitude !== undefined
              ? {
                  lat: parseFloat(endAddress.latitude),
                  lng: parseFloat(endAddress.longitude),
                }
              : null,
        };
      });

      // Mettre à jour la capacité utilisée pour chaque camion avec comparaisons strictes
      vehiclesArray = vehiclesArray.map((truck) => {
        const assignedDeliveries = deliveriesArray.filter(
          (delivery) => delivery.truck === truck.licensePlate
        );

        const totalWeight = assignedDeliveries.reduce(
          (sum, delivery) => sum + (parseFloat(delivery.weight) || 0),
          0
        );

        const totalLinearMeters = assignedDeliveries.reduce(
          (sum, delivery) => sum + (parseFloat(delivery.linearMeters) || 0),
          0
        );

        return {
          ...truck,
          usedCapacity: parseFloat(totalWeight.toFixed(2)),
          usedLinearMeters: parseFloat(totalLinearMeters.toFixed(2)),
        };
      });
    }

    // Mettre à jour les états avec les données préparées
    setTrucks(vehiclesArray);
    setDeliveries(deliveriesArray);

    // Log des couleurs des camions pour debug
    console.log(
      '🎨 Couleurs des camions:',
      vehiclesArray.map((v) => ({ id: v.id, key: v.key, color: v.color }))
    );

    // Stocker les coordonnées des livraisons directement depuis l'API
    const coordinates = {};
    deliveriesArray.forEach((delivery) => {
      if (delivery.coordinates) {
        coordinates[delivery.id] = delivery.coordinates;
      }
    });
    setDeliveryCoordinates(coordinates);

    // Utiliser les KPIs retournés par l'API si disponibles
    if (res && res.kpi) {
      console.log("📊 Utilisation des KPIs retournés par l'API:", res.kpi);

      // Calculer les KPIs manquants côté front (totalKilometers, totalCost, totalCO2, co2PerDelivery)
      calculateKPIsFromAPI(res.kpi, vehiclesArray, deliveriesArray);
    } else {
      // Fallback : calculer tous les KPIs côté front si l'API ne les retourne pas
      console.log("⚠️ Aucun KPI retourné par l'API, calcul côté front");
      calculateKPIs(vehiclesArray, deliveriesArray, routeData);
    }
  };

  // Ajouter une fonction pour gérer le clic sur un camion
  const handleTruckClick = (truckId) => {
    console.log(
      `🚛 Clic sur le camion ${truckId}. Camion actuellement sélectionné:`,
      selectedMapTruck
    );
    console.log(`🗺️ Données d'itinéraire disponibles:`, Object.keys(routeData));

    if (selectedMapTruck === truckId) {
      // Si on clique sur le même camion, on désélectionne
      console.log(`🔄 Désélection du camion ${truckId}`);
      setSelectedMapTruck(null);

      // Réinitialiser la vue de la carte pour voir toutes les livraisons
      if (mapRef.current) {
        const allDeliveryPoints = [];
        Object.values(deliveryCoordinates).forEach((coord) => {
          allDeliveryPoints.push([coord.lat, coord.lng]);
        });

        if (allDeliveryPoints.length > 0) {
          try {
            mapRef.current.fitBounds(allDeliveryPoints);
          } catch (error) {
            console.error("Erreur lors de l'ajustement de la carte", error);
          }
        }
      }
    } else {
      // Sinon, on sélectionne le camion
      console.log(`✅ Sélection du camion ${truckId}`);
      setSelectedMapTruck(truckId);

      // Récupérer l'itinéraire si nécessaire
      if (!routeData[truckId]) {
        console.log(
          `📍 Aucun itinéraire trouvé pour le camion ${truckId}, calcul en cours...`
        );
        // Récupérer juste l'itinéraire pour ce camion spécifique
        fetchRouteForSingleTruck(truckId);
      } else {
        console.log(
          `✅ Itinéraire déjà disponible pour le camion ${truckId}:`,
          routeData[truckId]
        );
      }

      // Ajuster la vue de la carte pour voir les livraisons de ce camion
      if (mapRef.current) {
        // Trouver toutes les livraisons assignées à ce camion et les trier par ordre
        const truck = trucks.find((t) => t.id === truckId);
        if (truck) {
          const truckDeliveries = getOrderedDeliveriesForTruck(truck);

          console.log(
            `📦 Livraisons trouvées pour le camion ${truckId}:`,
            truckDeliveries.length
          );
          console.log(
            `📋 Ordre des livraisons:`,
            truckDeliveries.map((d) => ({
              id: d.id,
              vroom_step_order: d.vroom_step_order,
              client: d.client,
            }))
          );

          const deliveryPoints = [];
          truckDeliveries.forEach((delivery) => {
            if (delivery.coordinates) {
              deliveryPoints.push([
                delivery.coordinates.lat,
                delivery.coordinates.lng,
              ]);
            }
          });

          if (deliveryPoints.length > 0) {
            try {
              mapRef.current.fitBounds(deliveryPoints);
            } catch (error) {
              console.error("Erreur lors de l'ajustement de la carte", error);
            }
          }
        }
      }
    }
  };

  // Fonction pour récupérer l'itinéraire d'un seul camion
  const fetchRouteForSingleTruck = async (truckId) => {
    setFetchingRoutes(true);

    // Vérifier que la configuration générale est chargée
    if (!isConfigValid) {
      console.warn('⚠️ Configuration générale non chargée, rechargement...');
      await loadGeneralConfig();
    }

    // Trouver le camion sélectionné
    const truck = trucks.find((t) => t.id === truckId);
    if (!truck) {
      setFetchingRoutes(false);
      return;
    }

    // Trouver toutes les livraisons assignées à ce camion et les trier par ordre
    const truckDeliveries = getOrderedDeliveriesForTruck(truck);

    console.log(
      `Livraisons triées par ordre pour le camion ${truck.id}:`,
      truckDeliveries.map((d) => ({
        id: d.id,
        vroom_step_order: d.vroom_step_order,
        client: d.client,
      }))
    );

    // Si le camion a des livraisons avec des coordonnées, récupérer son itinéraire
    if (truckDeliveries.length > 0) {
      const coordinatesArray = [];

      // Ajouter la factory comme point de départ si disponible
      let factoryLat = null;
      let factoryLng = null;

      // Essayer d'abord d'utiliser les coordonnées spécifiques du camion
      if (truck.factoryLatitude && truck.factoryLongitude) {
        factoryLat = truck.factoryLatitude;
        factoryLng = truck.factoryLongitude;
      }
      // Sinon utiliser les coordonnées globales de factory si disponibles
      else if (factoryCoordinates) {
        factoryLat = factoryCoordinates.lat;
        factoryLng = factoryCoordinates.lng;
      }

      // Ajouter le point de départ (factory)
      if (factoryLat && factoryLng) {
        coordinatesArray.push([factoryLng, factoryLat]);
      }

      // Ajouter tous les points intermédiaires
      for (let i = 0; i < truckDeliveries.length; i++) {
        if (
          truckDeliveries[i].coordinates &&
          truckDeliveries[i].coordinates.lat &&
          truckDeliveries[i].coordinates.lng
        ) {
          coordinatesArray.push([
            truckDeliveries[i].coordinates.lng,
            truckDeliveries[i].coordinates.lat,
          ]);
        }
      }

      // Ajouter la factory comme point de fin (retour au dépôt) si disponible
      if (factoryLat && factoryLng) {
        coordinatesArray.push([factoryLng, factoryLat]);
      }

      // Si on a au moins 2 points valides, faire la requête
      if (coordinatesArray.length >= 2) {
        try {
          // Créer le body pour la requête à l'API Akanea
          const requestBody = {
            language: 'fr-fr',
            maneuvers: true,
            preference: 'recommended',
            options: {
              vehicle_type: 'hgv',
              profile_params: {
                restrictions: {
                  axleload: 1,
                  hazmat: false,
                  height: 3,
                  length: 12,
                  width: 2.5,
                  weight: '19',
                },
              },
              avoid_features: ['highways', 'tollways'],
            },
            coordinates: coordinatesArray,
          };

          // Appel de l'API
          const response = await axios.post(
            'https://gis.akanea.com:8080/ors/v2/directions/driving-akplb/geojson',
            requestBody,
            {
              headers: {
                'Content-Type': 'application/geo+json;charset=UTF-8',
              },
            }
          );

          // Vérifier la réponse et l'ajouter aux données de route
          if (
            response.data &&
            response.data.features &&
            response.data.features.length > 0
          ) {
            // Ajouter les données de routage
            const newRouteData = { ...routeData };
            newRouteData[truck.id] = response.data;

            console.log(
              `🗺️ Mise à jour des données de route pour le camion ${truck.id}:`,
              {
                oldRouteDataKeys: Object.keys(routeData),
                newRouteDataKeys: Object.keys(newRouteData),
                coordinatesCount:
                  response.data.features[0].geometry.coordinates.length,
              }
            );

            setRouteData(newRouteData);

            setRouteErrorDeliveries((prev) => {
              const next = { ...prev };
              truckDeliveries.forEach((delivery) => {
                delete next[delivery.id];
              });
              return next;
            });

            // Mettre à jour la distance et la durée de la tournée dans les données du camion
            const distanceInKm =
              response.data.features[0].properties.summary.distance / 1000;
            const durationInHours =
              response.data.features[0].properties.summary.duration / 3600;

            console.log(
              `✅ Itinéraire calculé pour ${truck.key}: ${distanceInKm.toFixed(2)}km en ${durationInHours.toFixed(2)}h`
            );

            // Calculer les ETA pour ce camion
            try {
              const updatedDeliveries = await calculateETAs(
                truck,
                response.data,
                truckDeliveries,
                filters.selectedDate
              );

              // Mettre à jour les livraisons avec les nouveaux ETA
              if (updatedDeliveries.length > 0) {
                setDeliveries((prevDeliveries) => {
                  return prevDeliveries.map((delivery) => {
                    const updatedDelivery = updatedDeliveries.find(
                      (ud) => ud.id === delivery.id
                    );
                    return updatedDelivery || delivery;
                  });
                });

                console.log(
                  `🕐 ETA calculés et mis à jour pour ${updatedDeliveries.length} livraisons`
                );
              }
            } catch (error) {
              console.error(
                `❌ Erreur lors du calcul des ETA pour le camion ${truck.id}:`,
                error
              );
            }

            toast.success(
              `Itinéraire et ETA calculés pour le camion ${truck.key || truck.id}`
            );

            // CORRECTION: Rafraîchir les données depuis l'API pour actualiser les KPIs avec les nouveaux ETA
            // (conserve automatiquement les filtres via buildApiUrl)
            console.log(
              "🔄 Rafraîchissement des données après calcul d'itinéraire pour actualiser les KPIs..."
            );
            if (
              mutateScheduledData &&
              typeof mutateScheduledData === 'function'
            ) {
              try {
                mutateScheduledData();
                console.log(
                  "✅ Données rafraîchies avec succès après calcul d'itinéraire"
                );
              } catch (error) {
                console.error(
                  '❌ Erreur lors du rafraîchissement des données:',
                  error
                );
              }
            }
          }
        } catch (error) {
          console.error(
            `Erreur lors de la récupération de l'itinéraire pour le camion ${truck.id}:`,
            error
          );
          if (error.code === 'ECONNABORTED') {
            toast.error(
              `Timeout lors du calcul de l'itinéraire pour le camion ${truck.key || truck.id}`
            );
          } else if (error.response) {
            toast.error(
              `Erreur ${error.response.status} lors du calcul de l'itinéraire pour le camion ${truck.key || truck.id}`
            );
          } else {
            toast.error(
              `Impossible de calculer l'itinéraire pour le camion ${truck.key || truck.id}`
            );
          }

          // Log error but don't update trucks to avoid infinite loop
          console.log(`❌ Erreur pour ${truck.key}: ${error.message}`);
          setRouteErrorDeliveries((prev) => {
            const next = { ...prev };
            truckDeliveries.forEach((delivery) => {
              next[delivery.id] = true;
            });
            return next;
          });
        }
      } else {
        toast.warning(
          `Pas assez de points avec coordonnées pour calculer l'itinéraire du camion ${truck.key || truck.id}`
        );
        setRouteErrorDeliveries((prev) => {
          const next = { ...prev };
          truckDeliveries.forEach((delivery) => {
            next[delivery.id] = true;
          });
          return next;
        });
      }
    } else {
      toast.warning(
        `Le camion ${truck.key || truck.id} n'a pas assez de livraisons pour calculer un itinéraire`
      );
      setRouteErrorDeliveries((prev) => {
        const next = { ...prev };
        truckDeliveries.forEach((delivery) => {
          next[delivery.id] = true;
        });
        return next;
      });
    }

    setFetchingRoutes(false);
  };

  // Fonction pour obtenir les routes de tous les camions
  const fetchRouteForTrucks = async () => {
    setFetchingRoutes(true);

    // Vérifier que la configuration générale est chargée
    if (!isConfigValid) {
      console.warn('⚠️ Configuration générale non chargée, rechargement...');
      await loadGeneralConfig();
    }

    // Vérifier si nous avons des camions à traiter
    if (!trucks || trucks.length === 0) {
      console.error('Aucun camion disponible pour calculer les itinéraires');
      toast.error('Aucun camion disponible pour calculer les itinéraires');
      setFetchingRoutes(false);
      return;
    }

    const truckRoutes = { ...routeData };
    let hasError = false;
    const allUpdatedDeliveries = [];

    // console.log('Début du calcul des itinéraires');
    // console.log('Nombre de camions:', trucks.length);
    // console.log('Détails des camions:', trucks.map(t => ({
    //   id: t.id,
    //   key: t.key,
    //   licensePlate: t.licensePlate
    // })));

    // Pour chaque camion ayant des livraisons
    for (const truck of trucks) {
      // console.log(`-------- Traitement du camion ${truck.id || truck.key} --------`);
      // console.log('Détails du camion:', { id: truck.id, key: truck.key, licensePlate: truck.licensePlate });

      // Trouver toutes les livraisons assignées à ce camion et les trier par ordre
      const truckDeliveries = getOrderedDeliveriesForTruck(truck);

      console.log(
        `Livraisons triées par ordre pour le camion ${truck.id}:`,
        truckDeliveries.map((d) => ({
          id: d.id,
          vroom_step_order: d.vroom_step_order,
          client: d.client,
        }))
      );

      // console.log(`Nombre de livraisons pour ce camion: ${truckDeliveries.length}`);

      // Si le camion a des livraisons avec des coordonnées, récupérer son itinéraire
      if (truckDeliveries.length > 0) {
        const coordinatesArray = [];

        // Ajouter la factory comme point de départ si disponible
        let factoryLat = null;
        let factoryLng = null;

        // Essayer d'abord d'utiliser les coordonnées spécifiques du camion
        if (truck.factoryLatitude && truck.factoryLongitude) {
          factoryLat = truck.factoryLatitude;
          factoryLng = truck.factoryLongitude;
        }
        // Sinon utiliser les coordonnées globales de factory si disponibles
        else if (factoryCoordinates) {
          factoryLat = factoryCoordinates.lat;
          factoryLng = factoryCoordinates.lng;
        }

        // Ajouter le point de départ (factory)
        if (factoryLat && factoryLng) {
          coordinatesArray.push([factoryLng, factoryLat]);
        }

        // Ajouter tous les points intermédiaires
        for (let i = 0; i < truckDeliveries.length; i++) {
          if (
            truckDeliveries[i].coordinates &&
            truckDeliveries[i].coordinates.lat &&
            truckDeliveries[i].coordinates.lng
          ) {
            coordinatesArray.push([
              truckDeliveries[i].coordinates.lng,
              truckDeliveries[i].coordinates.lat,
            ]);
          }
        }

        // Ajouter la factory comme point de fin (retour au dépôt) si disponible
        if (factoryLat && factoryLng) {
          coordinatesArray.push([factoryLng, factoryLat]);
        }

        // console.log(`Nombre de points avec coordonnées (incluant factory): ${coordinatesArray.length}`);

        // Si on a au moins 2 points valides, faire la requête
        if (coordinatesArray.length >= 2) {
          try {
            // console.log('Envoi de la requête à l\'API Akanea');

            // Créer le body pour la requête à l'API Akanea
            const requestBody = {
              language: 'fr-fr',
              maneuvers: true,
              preference: 'recommended',
              options: {
                vehicle_type: 'hgv',
                profile_params: {
                  restrictions: {
                    axleload: 1,
                    hazmat: false,
                    height: 3,
                    length: 12,
                    width: 2.5,
                    weight: '19',
                  },
                },
                avoid_features: ['highways', 'tollways'],
              },
              coordinates: coordinatesArray,
            };

            // Appel de l'API
            const response = await axios.post(
              'https://gis.akanea.com:8080/ors/v2/directions/driving-akplb/geojson',
              requestBody,
              {
                headers: {
                  'Content-Type': 'application/geo+json;charset=UTF-8',
                },
                timeout: 30000, // Ajouter un timeout de 30 secondes
              }
            );

            console.log("Réponse reçue de l'API Akanea");

            // Vérifier la réponse et l'ajouter aux données de route
          if (
            response.data &&
            response.data.features &&
            response.data.features.length > 0
          ) {
              console.log('Données de route valides reçues');

              // Ajouter les données de routage
              truckRoutes[truck.id] = response.data;

            setRouteErrorDeliveries((prev) => {
              const next = { ...prev };
              truckDeliveries.forEach((delivery) => {
                delete next[delivery.id];
              });
              return next;
            });

              // Mettre à jour la distance et la durée de la tournée dans les données du camion
              const distanceInKm =
                response.data.features[0].properties.summary.distance / 1000;
              const durationInHours =
                response.data.features[0].properties.summary.duration / 3600;

              console.log(
                `Distance calculée: ${distanceInKm} km, Durée: ${durationInHours} heures`
              );

              // Calculer les ETA pour ce camion
              try {
                const updatedDeliveries = await calculateETAs(
                  truck,
                  response.data,
                  truckDeliveries,
                  filters.selectedDate
                );

                // Stocker les livraisons mises à jour pour la mise à jour finale
                if (updatedDeliveries.length > 0) {
                  allUpdatedDeliveries.push(...updatedDeliveries);
                  console.log(
                    `🕐 ETA calculés pour ${updatedDeliveries.length} livraisons du camion ${truck.key}`
                  );
                }
              } catch (error) {
                console.error(
                  `❌ Erreur lors du calcul des ETA pour le camion ${truck.id}:`,
                  error
                );
              }

              // Log success but don't update trucks to avoid infinite loop
              console.log(
                `✅ Itinéraire calculé pour ${truck.key}: ${distanceInKm.toFixed(2)}km en ${durationInHours.toFixed(2)}h`
              );
            } else {
              console.error("Réponse invalide de l'API:", response.data);
              toast.error(
                `Réponse invalide de l'API pour le camion ${truck.key || truck.id}`
              );
              hasError = true;
            }
          } catch (error) {
            console.error(
              `Erreur lors de la récupération de l'itinéraire pour le camion ${truck.id}:`,
              error
            );
            if (error.code === 'ECONNABORTED') {
              toast.error(
                `Timeout lors du calcul de l'itinéraire pour le camion ${truck.key || truck.id}`
              );
            } else if (error.response) {
              toast.error(
                `Erreur ${error.response.status} lors du calcul de l'itinéraire pour le camion ${truck.key || truck.id}`
              );
            } else {
              toast.error(
                `Impossible de calculer l'itinéraire pour le camion ${truck.key || truck.id}`
              );
          }

          // Log error but don't update trucks to avoid infinite loop
          console.log(`❌ Erreur pour ${truck.key}: ${error.message}`);
          hasError = true;
          setRouteErrorDeliveries((prev) => {
            const next = { ...prev };
            truckDeliveries.forEach((delivery) => {
              next[delivery.id] = true;
            });
            return next;
          });
          }
        } else {
          console.warn(
            `Pas assez de points avec coordonnées pour le camion ${truck.id || truck.key}`
          );
          toast.warning(
            `Pas assez de points avec coordonnées pour calculer l'itinéraire du camion ${truck.key || truck.id}. Il faut au moins 2 points.`
          );

          // Log error but don't update trucks to avoid infinite loop
          console.log(`❌ ${truck.key}: Pas assez de points avec coordonnées`);
          setRouteErrorDeliveries((prev) => {
            const next = { ...prev };
            truckDeliveries.forEach((delivery) => {
              next[delivery.id] = true;
            });
            return next;
          });
        }
      } else {
        console.warn(
          `Le camion ${truck.id || truck.key} n'a pas assez de livraisons pour calculer un itinéraire`
        );
        toast.warning(
          `Le camion ${truck.key || truck.id || truck.licensePlate} n'a pas assez de livraisons pour calculer un itinéraire. Il faut au moins 2 livraisons.`
        );

        // Log error but don't update trucks to avoid infinite loop
        console.log(`❌ ${truck.key}: Pas assez de livraisons`);
        setRouteErrorDeliveries((prev) => {
          const next = { ...prev };
          truckDeliveries.forEach((delivery) => {
            next[delivery.id] = true;
          });
          return next;
        });
      }
    }

    setRouteData(truckRoutes);
    setFetchingRoutes(false);

    // Mettre à jour l'état des livraisons avec les ETA calculés
    if (allUpdatedDeliveries.length > 0) {
      setDeliveries((prevDeliveries) => {
        return prevDeliveries.map((delivery) => {
          const updatedDelivery = allUpdatedDeliveries.find(
            (ud) => ud.id === delivery.id
          );
          return updatedDelivery || delivery;
        });
      });
      console.log(
        `🕐 Mis à jour ${allUpdatedDeliveries.length} livraisons avec leurs ETA`
      );
    }

    if (!hasError && Object.keys(truckRoutes).length > 0) {
      if (allUpdatedDeliveries.length > 0) {
        toast.success(
          `Itinéraires et ETA calculés avec succès pour ${allUpdatedDeliveries.length} livraisons`
        );
      } else {
        toast.success('Itinéraires calculés avec succès');
      }

      // CORRECTION: Rafraîchir les données depuis l'API pour actualiser les KPIs avec les nouveaux ETA
      // (conserve automatiquement les filtres via buildApiUrl)
      console.log(
        "🔄 Rafraîchissement des données après calcul d'itinéraires pour actualiser les KPIs..."
      );
      if (mutateScheduledData && typeof mutateScheduledData === 'function') {
        try {
          mutateScheduledData();
          console.log(
            "✅ Données rafraîchies avec succès après calcul d'itinéraires"
          );
        } catch (error) {
          console.error(
            '❌ Erreur lors du rafraîchissement des données:',
            error
          );
        }
      }
    }
    updateRouteBasedKPIs(trucks, deliveries, routeData);
  };

  // Fonction pour formater l'adresse complète (optimisée avec useCallback)
  const formatAddress = React.useCallback((street, zipcode, city) => {
    const parts = [];
    if (street) parts.push(street);
    if (zipcode) parts.push(zipcode);
    if (city) parts.push(city);

    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Si la date est invalide, retourner la chaîne d'origine

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  // Fonction pour calculer les ETA basées sur l'itinéraire et la configuration
  const calculateETAs = React.useCallback(
    async (truck, routeResponse, truckDeliveries, selectedDate) => {
      if (
        !routeResponse?.features?.[0]?.properties?.segments ||
        !generalConfig ||
        !generalConfig.default_start_time
      ) {
        console.warn(
          "❌ Configuration générale ou données d'itinéraire manquantes pour le calcul des ETA"
        );
        return [];
      }

      const segments = routeResponse.features[0].properties.segments;
      const startTime = generalConfig.default_start_time; // Format "HH:MM"
      // CORRECTION: Conversion explicite en nombre pour éviter les concaténations de strings
      const stopTimeMinutes =
        parseInt(generalConfig.average_time_per_stop, 10) || 15; // En minutes

      // Convertir l'heure de départ en minutes depuis minuit
      const [startHour, startMinute] = startTime.split(':').map(Number);
      let currentTimeMinutes = startHour * 60 + startMinute;

      console.log(`🕐 Calcul des ETA pour le camion ${truck.key}:`);
      console.log(`   - Heure de départ: ${startTime}`);
      console.log(`   - Temps d'arrêt par étape: ${stopTimeMinutes} minutes`);
      console.log(`   - Nombre de segments: ${segments.length}`);
      console.log(`   - Nombre de livraisons: ${truckDeliveries.length}`);

      const updatedDeliveries = [];

      // Pour chaque livraison
      for (let i = 0; i < truckDeliveries.length; i++) {
        const delivery = truckDeliveries[i];

        // Ajouter le temps de route jusqu'à cette livraison
        // Le segment [i] correspond au trajet du point i au point i+1
        // Pour la livraison i, on veut le segment [i] (du point précédent vers cette livraison)
        if (segments[i]) {
          // CORRECTION: Conversion explicite en nombre et validation
          const segmentDurationSeconds = parseFloat(segments[i].duration) || 0;
          const segmentDurationMinutes = Math.round(
            segmentDurationSeconds / 60
          );
          currentTimeMinutes += segmentDurationMinutes;

          console.log(
            `   - Livraison ${i + 1} (${delivery.client}): +${segmentDurationMinutes}min de route (segment ${i})`
          );
        }

        // Calculer l'heure d'arrivée (ETA)
        const etaHours = Math.floor(currentTimeMinutes / 60);
        const etaMinutes = currentTimeMinutes % 60;
        const etaTime = `${etaHours.toString().padStart(2, '0')}:${etaMinutes.toString().padStart(2, '0')}`;

        // Construire la datetime complète pour l'API (format: YYYY-MM-DD HH:MM:SS)
        // Utiliser la date de livraison de la delivery
        let etaDateTime = null;
        if (selectedDate && selectedDate !== 'N/A') {
          try {
            etaDateTime = `${selectedDate} ${etaTime}:00`;
          } catch (error) {
            console.error(
              `❌ Erreur lors de la construction de la datetime pour ${delivery.client}:`,
              error
            );
          }
        }

        // Si on n'arrive pas à construire la datetime, utiliser la date du filtre
        if (!etaDateTime) {
          etaDateTime = `${selectedDate} ${etaTime}:00`;
        }

        console.log(
          `   - ETA pour ${delivery.client}: ${etaTime} (datetime: ${etaDateTime})`
        );

        // Mettre à jour la livraison avec l'ETA (garder le format heure pour l'affichage)
        const updatedDelivery = {
          ...delivery,
          etaTime: etaTime,
        };
        updatedDeliveries.push(updatedDelivery);

        // Ajouter le temps d'arrêt pour cette livraison (sauf pour la dernière)
        if (i < truckDeliveries.length - 1) {
          currentTimeMinutes += stopTimeMinutes;
          console.log(`   - Temps d'arrêt: +${stopTimeMinutes}min`);
        }

        // Mettre à jour l'estimated_delivery via l'API avec la datetime complète
        if (delivery.tourPlanningId && etaDateTime) {
          console.log(delivery);
          try {
            await updateStepETA(delivery.id, etaDateTime);
          } catch (error) {
            console.error(
              `❌ Erreur lors de la mise à jour de l'ETA pour ${delivery.id}:`,
              error
            );
          }
        }
      }

      return updatedDeliveries;
    },
    [generalConfig, updateStepETA]
  );

  // Effet pour calculer les KPI - uniquement quand les données sont chargées (avec debounce optimisé)
  useEffect(() => {
    if (
      scheduledData &&
      !isLoadingScheduled &&
      trucks.length > 0 &&
      deliveries.length > 0
    ) {
      // Délai pour s'assurer que tous les états sont stabilisés et éviter les recalculs trop fréquents
      const timer = setTimeout(() => {
        updateRouteBasedKPIs(trucks, deliveries, routeData);
      }, 500); // Augmenté à 500ms pour un meilleur debounce

      return () => {
        clearTimeout(timer);
      };
    }
  }, [scheduledData, isLoadingScheduled, trucks, deliveries, routeData]); // Dépendances mises à jour

  // Effet pour redimensionner la carte après le chargement des données
  useEffect(() => {
    if (scheduledData && !isLoadingScheduled && mapRef.current) {
      // Attendre que le DOM soit mis à jour
      setTimeout(() => {
        if (mapRef.current) {
          try {
            mapRef.current.invalidateSize();
          } catch (error) {
            console.error(
              'Erreur lors du redimensionnement de la carte:',
              error
            );
          }
        }
      }, 100);
    }
  }, [scheduledData, isLoadingScheduled]);

  // Effet pour initialiser les icônes Leaflet
  useEffect(() => {
    // Corriger les icônes Leaflet par défaut
    try {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl:
          'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl:
          'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      });
      console.log('✅ Icônes Leaflet initialisées');
    } catch (error) {
      console.error(
        "Erreur lors de l'initialisation des icônes Leaflet:",
        error
      );
    }
  }, []);

  // Nouvelle fonction pour utiliser les KPIs de l'API et calculer les données manquantes côté front
  const calculateKPIsFromAPI = React.useCallback(
    (
      apiKpi,
      trucksData = trucks,
      deliveriesData = deliveries,
      routeDataParam = routeData
    ) => {
      console.log('📊 Calcul hybride des KPIs - API + Front');

      // CORRECTION: Calculer seulement les données manquantes côté front POUR LES CAMIONS FILTRÉS
      let totalKm = 0;

      // Obtenir les IDs des camions actuellement filtrés (affichés)
      const filteredTruckIds = trucksData.map((truck) => truck.id);

      // Ne prendre en compte que les routes des camions filtrés
      Object.entries(routeDataParam).forEach(([truckId, route]) => {
        // Vérifier si ce camion est dans les données filtrées
        if (
          filteredTruckIds.includes(parseInt(truckId)) ||
          filteredTruckIds.includes(truckId)
        ) {
          if (
            route &&
            route.features &&
            route.features[0] &&
            route.features[0].properties
          ) {
            totalKm += route.features[0].properties.summary.distance / 1000;
          }
        }
      });

      // Si pas de données de route mais que les camions filtrés ont des distances
      if (totalKm === 0) {
        trucksData.forEach((truck) => {
          if (truck.tourDistance) {
            totalKm += truck.tourDistance;
          }
        });
      }

      // Calculer le coût total (estimé à 1.2€ par km)
      const costPerKm = 1.2;
      const totalCost = totalKm * costPerKm;

      // Calculer l'impact CO2 (estimé à 0.8kg par km)
      const co2PerKm = 0.8;
      const totalCO2 = totalKm * co2PerKm;

      // Calculer le CO2 par livraison
      const co2PerDelivery =
        apiKpi.totalDeliveries > 0 ? totalCO2 / apiKpi.totalDeliveries : 0;

      // Utiliser les KPIs de l'API et compléter avec les calculs front
      setKpiData({
        totalKilometers: totalKm, // Calculé côté front (nécessite les itinéraires)
        totalWeight: apiKpi.totalWeight || 0, // De l'API
        totalTrucks: apiKpi.totalTrucks || 0, // De l'API
        totalDeliveries: apiKpi.totalDeliveries || 0, // De l'API
        totalCost: totalCost, // Calculé côté front (dépend des km)
        totalCO2: totalCO2, // Calculé côté front (dépend des km)
        co2PerDelivery: co2PerDelivery, // Calculé côté front (dépend des km)
        totalMissions: apiKpi.totalMissions || 0, // De l'API
        unassignedMissions: apiKpi.unassignedMissions || 0, // De l'API
        missionsWithoutETA: apiKpi.missionsWithoutETA || 0, // De l'API
      });

      console.log('✅ KPIs mis à jour:', {
        fromAPI: {
          totalWeight: apiKpi.totalWeight,
          totalTrucks: apiKpi.totalTrucks,
          totalDeliveries: apiKpi.totalDeliveries,
          totalMissions: apiKpi.totalMissions,
          unassignedMissions: apiKpi.unassignedMissions,
          missionsWithoutETA: apiKpi.missionsWithoutETA,
        },
        fromFront: {
          totalKilometers: totalKm,
          totalCost: totalCost,
          totalCO2: totalCO2,
          co2PerDelivery: co2PerDelivery,
        },
      });
    },
    []
  ); // Dépendances vides car la fonction utilise ses propres paramètres

  // Fonction spécialisée pour recalculer seulement les KPIs liés aux itinéraires
  // Utilisée quand des routes sont calculées côté front et qu'on veut préserver les KPIs de l'API
  const updateRouteBasedKPIs = React.useCallback(
    (
      trucksData = trucks,
      deliveriesData = deliveries,
      routeDataParam = routeData
    ) => {
      console.log('🛣️ Mise à jour des KPIs liés aux itinéraires uniquement');

      // CORRECTION: Calculer le total des kilomètres SEULEMENT pour les camions filtrés
      let totalKm = 0;

      // Obtenir les IDs des camions actuellement filtrés (affichés)
      const filteredTruckIds = trucksData.map((truck) => truck.id);

      // Ne prendre en compte que les routes des camions filtrés
      Object.entries(routeDataParam).forEach(([truckId, route]) => {
        // Vérifier si ce camion est dans les données filtrées
        if (
          filteredTruckIds.includes(parseInt(truckId)) ||
          filteredTruckIds.includes(truckId)
        ) {
          if (
            route &&
            route.features &&
            route.features[0] &&
            route.features[0].properties
          ) {
            totalKm += route.features[0].properties.summary.distance / 1000;
          }
        }
      });

      // Si pas de données de route mais que les camions filtrés ont des distances
      if (totalKm === 0) {
        trucksData.forEach((truck) => {
          if (truck.tourDistance) {
            totalKm += truck.tourDistance;
          }
        });
      }

      // Calculer le coût total (estimé à 1.2€ par km)
      const costPerKm = 1.2;
      const totalCost = totalKm * costPerKm;

      // Calculer l'impact CO2 (estimé à 0.8kg par km)
      const co2PerKm = 0.8;
      const totalCO2 = totalKm * co2PerKm;

      // Calculer le CO2 par livraison en utilisant les données actuelles des KPIs
      const co2PerDelivery =
        kpiData.totalDeliveries > 0 ? totalCO2 / kpiData.totalDeliveries : 0;

      // Mettre à jour seulement les KPIs liés aux itinéraires
      setKpiData((prevKpiData) => ({
        ...prevKpiData, // Préserver tous les KPIs existants (notamment ceux de l'API)
        totalKilometers: totalKm, // Mettre à jour seulement les KPIs calculés
        totalCost: totalCost,
        totalCO2: totalCO2,
        co2PerDelivery: co2PerDelivery,
      }));

      console.log('✅ KPIs liés aux itinéraires mis à jour:', {
        totalKilometers: totalKm,
        totalCost: totalCost,
        totalCO2: totalCO2,
        co2PerDelivery: co2PerDelivery,
      });
    },
    [kpiData.totalDeliveries] // Dépendance sur totalDeliveries pour le calcul du CO2 par livraison
  );

  // Fonction utilitaire pour convertir le format de temps HH:mm:ss en HH:mm si nécessaire
  const formatTimeToHHMM = (time) => {
    if (!time) return time;
    // Si le format inclut des secondes (HH:mm:ss), on les enlève
    if (time.length === 8 && time.includes(':')) {
      return time.substring(0, 5); // Garde seulement HH:mm
    }
    return time;
  };

  // Fonction pour optimiser l'ordre des étapes d'un camion
  const optimizeVehicleSequence = async (truck, skipToast = false) => {
    try {
      // Obtenir les étapes assignées à ce camion
      const truckDeliveries = getOrderedDeliveriesForTruck(truck);
      
      if (truckDeliveries.length < 2) {
        if (!skipToast) {
          toast.warning(`Le camion ${truck.key || truck.id} doit avoir au moins 2 étapes pour être optimisé`);
        }
        return null;
      }

      // Préparer les données pour l'API
      const assignedSteps = truckDeliveries.map(delivery => ({
        stepId: delivery.id,
        deliveryDetailId: delivery.tourPlanningId,
        location: delivery.coordinates ? [delivery.coordinates.lng, delivery.coordinates.lat] : null,
        weight: delivery.weight,
        linearMeters: delivery.linearMeters || 0,
        serviceTime: 15, // Temps de service par défaut en minutes
        clientName: delivery.clientName || delivery.client,
        address: delivery.address
      })).filter(step => step.location); // Filtrer les étapes sans coordonnées

      if (assignedSteps.length < 2) {
        if (!skipToast) {
          toast.warning(`Le camion ${truck.key || truck.id} doit avoir au moins 2 étapes avec coordonnées pour être optimisé`);
        }
        return null;
      }

      // Paramètres d'optimisation
      const optimizationParams = {
        vehicleId: truck.id,
        vehicleKey: truck.key || truck.id,
        assignedSteps: assignedSteps,
        currentPosition: factoryCoordinates ? [factoryCoordinates.lng, factoryCoordinates.lat] : null,
        timeConstraints: {
          startTime: formatTimeToHHMM(generalConfig?.default_start_time) || "08:00",
          endTime: formatTimeToHHMM("18:00")
        },
        optimizationOptions: {
          returnGeometry: true,
          respectTimeWindows: false
        }
      };

      // Appeler l'API d'optimisation via le hook
      const result = await optimizeSequence(optimizationParams);

      if (result && result.optimizedSequence) {
        const gains = result.improvements; // Utiliser 'improvements' au lieu de 'gains'

        // Afficher le message de succès avec les gains
        if (!skipToast) {
          let successMessage = `Optimisation réussie pour le camion ${truck.key || truck.id}`;
          if (gains) {
            const savingsDetails = [];
            if (gains.totalTimeReduction > 0) {
              savingsDetails.push(`${Math.round(gains.totalTimeReduction)} min économisées`);
            }
            if (gains.totalDistanceReduction > 0) {
              savingsDetails.push(`${gains.totalDistanceReduction.toFixed(2)} km économisés`);
            }
            if (savingsDetails.length > 0) {
              successMessage += ` (${savingsDetails.join(', ')})`;
            }
          }
          
          toast.success(successMessage);
        }

        // Nettoyer l'ancien itinéraire pour ce camion
        setRouteData((previousRouteData) => {
          if (!previousRouteData[truck.id]) {
            return previousRouteData;
          }
          const { [truck.id]: _removedRoute, ...remainingRoutes } = previousRouteData;
          return remainingRoutes;
        });

        // Sauvegarder les stats d'optimisation
        if (gains) {
          setVehicleOptimizationStats((prev) => ({
            ...prev,
            [truck.id]: {
              totalTimeReduction: gains.totalTimeReduction,
              totalDistanceReduction: gains.totalDistanceReduction,
              totalDistanceKm: gains.totalDistanceKm,
              totalDurationMinutes: gains.totalDurationMinutes,
              updatedAt: new Date().toISOString(),
            },
          }));
        }

        // Rafraîchir les données depuis l'API pour récupérer l'ordre mis à jour et les ETAs
        console.log('🔄 Rafraîchissement des données après optimisation...');
        try {
          const mutateResult = mutateScheduledData?.();
          if (mutateResult && typeof mutateResult.then === 'function') {
            await mutateResult;
          }
          // Attendre un peu que React traite les nouvelles données
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (refreshError) {
          console.error('❌ Erreur lors du rafraîchissement après optimisation:', refreshError);
        }

        // Calculer le nouvel itinéraire avec les données fraîches
        if (!skipToast) {
          console.log("🗺️ Recalcul de l'itinéraire après optimisation...");
          await fetchRouteForSingleTruck(truck.id);
        }

        return result;
      }
      
      return null;
    } catch (error) {
      // L'erreur est déjà gérée par le hook et affichée via console.error
      // On peut ajouter un toast supplémentaire si nécessaire
      if (!skipToast) {
        toast.error(error.message);
      }
      return null;
    }
  };

  // Fonction pour l'optimisation complète : affectation globale + optimisation de séquence + calcul d'itinéraires
  const handleFullOptimization = async () => {
    setIsFullOptimizationRunning(true);
    
    try {
      // Étape 1 : Affectation globale avec VROOM
      console.log('🚀 Étape 1/3 : Affectation globale des livraisons...');
      const result = await optimizeAllSteps({
        tourDate: filters.selectedDate,
      });

      const totalAssignments = result?.routes?.reduce((acc, route) => acc + ((route?.steps || []).filter((step) => step.jobId !== undefined).length || 0), 0);
      const unassignedJobs = Array.isArray(result?.unassignedJobs) ? result.unassignedJobs.length : 0;

      if (totalAssignments === 0) {
        toast.warning('Aucune étape assignée');
        setIsFullOptimizationRunning(false);
        return;
      }

      // Rafraîchir les données pour avoir les nouvelles affectations
      console.log('🔄 Rafraîchissement des données après affectation...');
      const mutateResult = mutateScheduledData?.();
      if (mutateResult && typeof mutateResult.then === 'function') {
        await mutateResult;
      }

      // Attendre que les données soient bien traitées par React
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Étape 2 : Optimiser la séquence de chaque camion
      console.log('🚀 Étape 2/3 : Optimisation de la séquence de chaque camion...');
      console.log('🔍 Trucks disponibles:', trucks.length);
      console.log('🔍 Deliveries disponibles:', deliveries.length);
      
      const trucksWithDeliveries = trucks.filter(truck => {
        const truckDeliveries = deliveries.filter(d => d.vehicleId === truck.id || d.truck === truck.licensePlate);
        console.log(`🚛 Camion ${truck.key || truck.id}: ${truckDeliveries.length} livraisons`);
        return truckDeliveries.length >= 2;
      });

      console.log(`📊 Camions avec au moins 2 livraisons: ${trucksWithDeliveries.length}`);

      if (trucksWithDeliveries.length > 0) {
        for (const truck of trucksWithDeliveries) {
          console.log(`🔄 Optimisation de la séquence du camion ${truck.key || truck.id}...`);
          try {
            await optimizeVehicleSequence(truck, true); // skipToast = true pour éviter trop de notifications
            console.log(`✅ Séquence optimisée pour le camion ${truck.key || truck.id}`);
            // Petit délai entre chaque camion pour éviter de surcharger l'API
            await new Promise(resolve => setTimeout(resolve, 300));
          } catch (error) {
            console.error(`❌ Erreur lors de l'optimisation du camion ${truck.key || truck.id}:`, error);
          }
        }
        console.log(`✅ Séquence optimisée pour ${trucksWithDeliveries.length} camion(s)`);
      } else {
        console.warn('⚠️ Aucun camion avec au moins 2 livraisons à optimiser');
      }

      // Rafraîchir à nouveau les données après l'optimisation des séquences
      console.log('🔄 Rafraîchissement final des données...');
      const finalMutateResult = mutateScheduledData?.();
      if (finalMutateResult && typeof finalMutateResult.then === 'function') {
        await finalMutateResult;
      }

      // Attendre que les données soient bien traitées
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Étape 3 : Calculer les itinéraires
      console.log('🚀 Étape 3/3 : Calcul des itinéraires...');
      await fetchRouteForTrucks();

      // Message de succès final
      const summaryDistance = result?.summary?.totalDistance || 0;
      const summaryDuration = result?.summary?.totalDuration || 0;

      toast.success(
        `Optimisation complète terminée ! ${totalAssignments} étape(s) assignée(s)` +
          (unassignedJobs > 0 ? `, ${unassignedJobs} non assignée(s)` : '') +
          ` (${summaryDistance.toFixed ? summaryDistance.toFixed(2) : summaryDistance} km, ${summaryDuration} min)`
      );

    } catch (error) {
      console.error('❌ Erreur lors de l\'optimisation complète:', error);
      toast.error(error.message || 'Erreur lors de l\'optimisation complète');
    } finally {
      setIsFullOptimizationRunning(false);
    }
  };

  // Fonction pour calculer les KPI avec paramètres explicites (optimisée avec useCallback)
  // Conservée pour le fallback si l'API ne retourne pas de KPIs
  const calculateKPIs = React.useCallback(
    (
      trucksData = trucks,
      deliveriesData = deliveries,
      routeDataParam = routeData
    ) => {
      console.log('📊 Calcul complet des KPIs côté front (fallback)');

      // CORRECTION: Calculer le total des kilomètres POUR LES CAMIONS FILTRÉS SEULEMENT
      let totalKm = 0;

      // Obtenir les IDs des camions actuellement filtrés (affichés)
      const filteredTruckIds = trucksData.map((truck) => truck.id);

      // Ne prendre en compte que les routes des camions filtrés
      Object.entries(routeDataParam).forEach(([truckId, route]) => {
        // Vérifier si ce camion est dans les données filtrées
        if (
          filteredTruckIds.includes(parseInt(truckId)) ||
          filteredTruckIds.includes(truckId)
        ) {
          if (
            route &&
            route.features &&
            route.features[0] &&
            route.features[0].properties
          ) {
            totalKm += route.features[0].properties.summary.distance / 1000;
          }
        }
      });

      // Si pas de données de route mais que les camions filtrés ont des distances
      if (totalKm === 0) {
        trucksData.forEach((truck) => {
          if (truck.tourDistance) {
            totalKm += truck.tourDistance;
          }
        });
      }

      // Calculer le poids total chargé
      const totalWeight = trucksData.reduce(
        (sum, truck) => sum + truck.usedCapacity,
        0
      );

      // Calculer le nombre de camions utilisés (ceux qui ont au moins une livraison)
      const trucksWithDeliveries = trucksData.filter((truck) =>
        deliveriesData.some((d) => d.truck === truck.licensePlate)
      ).length;

      // Calculer le coût total (estimé à 1.2€ par km)
      const costPerKm = 1.2;
      const totalCost = totalKm * costPerKm;

      // Calculer l'impact CO2 (estimé à 0.8kg par km)
      const co2PerKm = 0.8;
      const totalCO2 = totalKm * co2PerKm;

      // Calculer le CO2 par livraison
      const co2PerDelivery =
        deliveriesData.length > 0 ? totalCO2 / deliveriesData.length : 0;

      // Calculer les nouvelles métriques de missions
      const totalMissions = deliveriesData.length;
      const unassignedMissions = deliveriesData.filter(
        (d) => !d.vehicleId && (d.truck === 'Non affecté' || !d.truck)
      ).length;
      const missionsWithoutETA = deliveriesData.filter(
        (d) => !d.etaTime
      ).length;

      setKpiData({
        totalKilometers: totalKm,
        totalWeight: totalWeight,
        totalTrucks: trucksWithDeliveries,
        totalDeliveries: deliveriesData.length,
        totalCost: totalCost,
        totalCO2: totalCO2,
        co2PerDelivery: co2PerDelivery,
        totalMissions: totalMissions,
        unassignedMissions: unassignedMissions,
        missionsWithoutETA: missionsWithoutETA,
      });
    },
    []
  ); // Dépendances vides car la fonction utilise ses propres paramètres

  // Fonction pour mettre à jour la carte après une réaffectation (optimisée avec useCallback)
  const updateMapAfterReassignment = React.useCallback(() => {
    // Rafraîchir les données depuis l'API sans recharger la page
    console.log('🔄 Rafraîchissement des données après réaffectation...');

    // Effacer les données de routage puisque les affectations ont changé
    setRouteData({});

    // Réinitialiser la sélection de camion sur la carte
    setSelectedMapTruck(null);

    if (mutateScheduledData && typeof mutateScheduledData === 'function') {
      try {
        // Utiliser mutate pour rafraîchir les données depuis l'API
        const result = mutateScheduledData();

        // Vérifier si le résultat est une Promise
        if (result && typeof result.then === 'function') {
          result
            .then(() => {
              console.log('✅ Données rafraîchies avec succès');
              toast.success(
                'Réaffectation terminée avec succès - Recalculez les itinéraires si nécessaire'
              );
            })
            .catch((error) => {
              console.error(
                '❌ Erreur lors du rafraîchissement des données:',
                error
              );
              toast.error('Erreur lors du rafraîchissement des données');
            });
        } else {
          // Si mutate ne retourne pas une Promise, on considère que c'est réussi
          console.log('✅ Données rafraîchies (synchrone)');
          toast.success(
            'Réaffectation terminée avec succès - Recalculez les itinéraires si nécessaire'
          );
        }
      } catch (error) {
        console.error(
          "❌ Erreur lors de l'appel de mutateScheduledData:",
          error
        );
        toast.error('Erreur lors du rafraîchissement des données');
      }
    } else {
      console.warn('⚠️ mutateScheduledData non disponible');
      toast.success(
        'Réaffectation terminée - Actualisez la page pour voir les changements'
      );
    }
  }, [mutateScheduledData]);

  // Fonction pour générer une couleur aléatoire
  const generateRandomColor = () => {
    // Liste de couleurs prédéfinies
    const colors = [
      '#FF0000', // Rouge
      '#00FF00', // Vert
      '#0000FF', // Bleu
      '#FFFF00', // Jaune
      '#00FFFF', // Cyan
      '#FF00FF', // Magenta
      '#000000', // Noir
      '#FFFFFF', // Blanc
      '#808080', // Gris
      '#FFA500', // Orange
      '#FFC0CB', // Rose
      '#800080', // Violet
      '#A52A2A', // Marron
      '#40E0D0', // Turquoise
      '#FFD700', // Or
      '#C0C0C0', // Argent
      '#90EE90', // Vert clair
      '#ADD8E6', // Bleu clair
      '#FF6347', // Rouge clair
      '#D8BFD8', // Violet clair
    ];

    // On récupère l'index courant en fonction du nombre de couleurs déjà utilisées
    const colorIndex = Object.keys(truckColors).length % colors.length;
    return colors[colorIndex];
  };

  // Fonction pour obtenir la couleur d'un camion depuis l'API
  const getTruckColor = React.useCallback(
    (truckId) => {
      // Recherche avec comparaison stricte et conversion de types
      const truck = trucks.find((t) => {
        const match = String(t.id) === String(truckId) || t.id === truckId;
        return match;
      });

      if (truck && truck.color) {
        return truck.color;
      }

      // Fallback vers l'ancien système si pas de couleur API
      if (truckColors[truckId]) {
        return truckColors[truckId];
      }

      return '#cccccc';
    },
    [trucks, truckColors]
  );

  // Effet pour initialiser les couleurs des camions quand ils changent (fallback pour l'ancien système)
  React.useEffect(() => {
    if (trucks.length > 0) {
      const newColors = { ...truckColors };
      let hasNewColor = false;

      trucks.forEach((truck) => {
        // Seulement générer une couleur si le camion n'a pas de couleur API et pas de couleur dans l'ancien système
        if (!truck.color && !newColors[truck.id]) {
          newColors[truck.id] = generateRandomColor();
          hasNewColor = true;
        }
      });

      if (hasNewColor) {
        setTruckColors(newColors);
      }
    }
  }, [trucks.length]); // Seulement quand le nombre de camions change

  // Fonction pour ouvrir la modale d'assignation
  const openAssignModal = (delivery) => {
    console.log('Ouverture modale pour livraison:', delivery);

    if (!delivery || (delivery.id === undefined && delivery.id === null)) {
      console.error('Livraison invalide - ID manquant:', delivery);
      toast.error('Erreur: ID de livraison manquant');
      return;
    }

    setSelectedDelivery(delivery);
    setSelectedTruck(null);
    setShowAssignModal(true);
  };

  // Fonction pour fermer la modale d'assignation
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedDelivery(null);
    setSelectedTruck(null);
  };

  // Fonction pour gérer la sélection d'un camion
  const handleTruckSelection = (truck) => {
    setSelectedTruck(truck);
  };

  // Fonction modifiée pour confirmer l'assignation d'une commande à un camion
  const confirmAssignment = async () => {
    if (!selectedDelivery || !selectedTruck) {
      console.error('Données manquantes:', { selectedDelivery, selectedTruck });
      return;
    }

    // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
    const previousDeliveries = JSON.parse(JSON.stringify(deliveries));
    const previousTrucks = JSON.parse(JSON.stringify(trucks));

    console.log('=== DÉBUT RÉAFFECTATION ===');
    console.log('Livraison sélectionnée:', selectedDelivery);
    console.log('ID de la livraison:', selectedDelivery.id);
    console.log("Type de l'ID:", typeof selectedDelivery.id);
    console.log('Camion sélectionné:', selectedTruck);

    // Mettre à jour la commande avec le nouveau camion - SEULEMENT LA LIVRAISON SÉLECTIONNÉE
    const updatedDeliveries = deliveries.map((delivery) => {
      // Vérification des IDs avant la comparaison
      const deliveryIdStr = String(delivery.id);
      const selectedIdStr = String(selectedDelivery.id);

      console.log(
        `Comparaison: ${deliveryIdStr} === ${selectedIdStr} ?`,
        deliveryIdStr === selectedIdStr
      );

      // Conversion stricte des IDs pour la comparaison
      if (deliveryIdStr === selectedIdStr) {
        console.log(
          `✅ Mise à jour de la livraison ${delivery.id} vers le camion ${selectedTruck.key || selectedTruck.id}`
        );
        return {
          ...delivery,
          truck: selectedTruck.licensePlate,
          vehicleId: selectedTruck.id,
        };
      }
      // Toutes les autres livraisons restent inchangées
      return delivery;
    });

    // Recalculer les charges des camions avec comparaisons strictes
    const updatedTrucks = trucks.map((truck) => {
      const assignedDeliveries = updatedDeliveries.filter(
        (delivery) => delivery.truck === truck.licensePlate
      );

      const totalWeight = assignedDeliveries.reduce(
        (sum, delivery) => sum + (parseFloat(delivery.weight) || 0),
        0
      );

      const totalLinearMeters = assignedDeliveries.reduce(
        (sum, delivery) => sum + (parseFloat(delivery.linearMeters) || 0),
        0
      );

      console.log(
        `Camion ${truck.key || truck.id}: ${assignedDeliveries.length} livraisons, ${totalWeight}kg, ${totalLinearMeters}ml`
      );

      return {
        ...truck,
        usedCapacity: parseFloat(totalWeight.toFixed(2)),
        usedLinearMeters: parseFloat(totalLinearMeters.toFixed(2)),
      };
    });

    console.log('=== FIN RÉAFFECTATION ===');

    // Optimistic update: Mettre à jour les états immédiatement
    setDeliveries(updatedDeliveries);
    setTrucks(updatedTrucks);

    // Mettre à jour la carte et recalculer les itinéraires
    updateMapAfterReassignment();

    // Fermer la modale
    closeAssignModal();

    try {
      // Appel API avec les données requises
      await assignSteps({
        tour_planning_ids: [selectedDelivery.tourPlanningId],
        vehicle_key: selectedTruck.key || selectedTruck.id,
        tour_date: filters.selectedDate,
      });

      // Succès : afficher un message de confirmation et rafraîchir les données
      toast.success(
        `Commande #${selectedDelivery.id} assignée au camion ${selectedTruck.licensePlate}`
      );
      mutateScheduledData();

      // Recalculer l'itinéraire et les ETA pour le camion affecté
      await fetchRouteForSingleTruck(selectedTruck.id);
    } catch (error) {
      // Erreur : rollback des états et afficher l'erreur
      console.error("Erreur lors de l'affectation:", error);
      setDeliveries(previousDeliveries);
      setTrucks(previousTrucks);
      updateMapAfterReassignment();
      // Le toast d'erreur est déjà géré par le hook
    }
  };

  const getOrderedDeliveriesForTruck = React.useCallback(
    (truck) => {
      const truckDeliveries = deliveries.filter(
        (delivery) =>
          delivery.vehicleId === truck.id ||
          delivery.truck === truck.licensePlate ||
          delivery.truck === truck.key
      );

      // Trier par ordre (step.vroom_step_order de l'API)
      return truckDeliveries.sort((a, b) => {
        const orderA = a.vroom_step_order || 0;
        const orderB = b.vroom_step_order || 0;
        return orderA - orderB;
      });
    },
    [deliveries]
  );

  // Fonction pour la sélection d'une livraison avec comparaisons strictes
  const toggleDeliverySelection = (delivery) => {
    if (selectedDeliveries.some((d) => String(d.id) === String(delivery.id))) {
      setSelectedDeliveries(
        selectedDeliveries.filter((d) => String(d.id) !== String(delivery.id))
      );
    } else {
      setSelectedDeliveries([...selectedDeliveries, delivery]);
    }
  };

  // Fonction pour ouvrir la modale d'assignation en masse
  const openBulkAssignModal = () => {
    if (selectedDeliveries.length === 0) {
      toast.error('Veuillez sélectionner au moins une livraison à réaffecter');
      return;
    }
    setSelectedDelivery(null);
    setSelectedTruck(null);
    setShowAssignModal(true);
  };

  // Modification similaire pour la fonction confirmBulkAssignment
  const confirmBulkAssignment = async () => {
    if (!selectedTruck) return;

    // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
    const previousDeliveries = JSON.parse(JSON.stringify(deliveries));
    const previousTrucks = JSON.parse(JSON.stringify(trucks));
    const previousSelectedDeliveries = [...selectedDeliveries];

    // Calculer le poids total des livraisons sélectionnées
    const totalWeight = selectedDeliveries.reduce(
      (sum, delivery) => sum + parseFloat(delivery.weight || 0),
      0
    );

    // Calculer les mètres linéaires totaux des livraisons sélectionnées
    const totalLinearMeters = selectedDeliveries.reduce(
      (sum, delivery) => sum + parseFloat(delivery.linearMeters || 0),
      0
    );

    // Vérifier si le camion peut supporter le poids avec comparaisons strictes
    const assignedWeight = deliveries
      .filter(
        (d) => !selectedDeliveries.some((sd) => String(sd.id) === String(d.id))
      ) // Exclure les livraisons sélectionnées
      .filter((d) => d.truck === selectedTruck.licensePlate)
      .reduce((sum, d) => sum + parseFloat(d.weight || 0), 0);

    // Vérifier si le camion peut supporter les mètres linéaires avec comparaisons strictes
    const assignedLinearMeters = deliveries
      .filter(
        (d) => !selectedDeliveries.some((sd) => String(sd.id) === String(d.id))
      ) // Exclure les livraisons sélectionnées
      .filter((d) => d.truck === selectedTruck.licensePlate)
      .reduce((sum, d) => sum + parseFloat(d.linearMeters || 0), 0);

    const newTotalWeight = assignedWeight + totalWeight;
    const newTotalLinearMeters = assignedLinearMeters + totalLinearMeters;

    // Vérifier les surcharges
    let warningMessage = '';
    if (
      newTotalWeight > selectedTruck.capacity &&
      selectedTruck.linearCapacity > 0 &&
      newTotalLinearMeters > selectedTruck.linearCapacity
    ) {
      warningMessage = `Attention : Cette assignation causera une surcharge de ${(newTotalWeight - selectedTruck.capacity).toFixed(2)} kg et ${(newTotalLinearMeters - selectedTruck.linearCapacity).toFixed(2)} ml sur le camion ${selectedTruck.key || selectedTruck.id}. Voulez-vous continuer ?`;
    } else if (newTotalWeight > selectedTruck.capacity) {
      warningMessage = `Attention : Cette assignation causera une surcharge de ${(newTotalWeight - selectedTruck.capacity).toFixed(2)} kg sur le camion ${selectedTruck.key || selectedTruck.id}. Voulez-vous continuer ?`;
    } else if (
      selectedTruck.linearCapacity > 0 &&
      newTotalLinearMeters > selectedTruck.linearCapacity
    ) {
      warningMessage = `Attention : Cette assignation causera une surcharge de ${(newTotalLinearMeters - selectedTruck.linearCapacity).toFixed(2)} ml sur le camion ${selectedTruck.key || selectedTruck.id}. Voulez-vous continuer ?`;
    }

    if (warningMessage) {
      const confirm = window.confirm(warningMessage);
      if (!confirm) return;
    }

    // Mettre à jour les livraisons avec comparaisons strictes
    const updatedDeliveries = deliveries.map((delivery) => {
      if (
        selectedDeliveries.some((d) => String(d.id) === String(delivery.id))
      ) {
        console.log(
          `✅ Mise à jour en masse de la livraison ${delivery.id} vers le camion ${selectedTruck.key || selectedTruck.id}`
        );
        return {
          ...delivery,
          truck: selectedTruck.licensePlate,
          vehicleId: selectedTruck.id,
        };
      }
      return delivery;
    });

    // Recalculer les charges des camions avec comparaisons strictes
    const updatedTrucks = trucks.map((truck) => {
      const assignedDeliveries = updatedDeliveries.filter(
        (delivery) => delivery.truck === truck.licensePlate
      );

      const totalWeight = assignedDeliveries.reduce(
        (sum, delivery) => sum + (parseFloat(delivery.weight) || 0),
        0
      );

      const totalLinearMeters = assignedDeliveries.reduce(
        (sum, delivery) => sum + (parseFloat(delivery.linearMeters) || 0),
        0
      );

      console.log(
        `Camion ${truck.key || truck.id}: ${assignedDeliveries.length} livraisons, ${totalWeight}kg, ${totalLinearMeters}ml`
      );

      return {
        ...truck,
        usedCapacity: parseFloat(totalWeight.toFixed(2)),
        usedLinearMeters: parseFloat(totalLinearMeters.toFixed(2)),
      };
    });

    console.log('=== FIN RÉAFFECTATION EN MASSE ===');

    // Optimistic update: Mettre à jour les états immédiatement
    setDeliveries(updatedDeliveries);
    setTrucks(updatedTrucks);
    setSelectedDeliveries([]);

    // Mettre à jour la carte et recalculer les itinéraires
    updateMapAfterReassignment();

    // Fermer la modale
    closeAssignModal();

    try {
      // Appel API avec les données requises
      const tourPlanningIds = selectedDeliveries.map(
        (delivery) => delivery.tourPlanningId
      );
      await assignSteps({
        tour_planning_ids: tourPlanningIds,
        vehicle_key: selectedTruck.key || selectedTruck.id,
        tour_date: filters.selectedDate,
      });

      toast.success(
        `${previousSelectedDeliveries.length} livraison(s) assignée(s) au camion ${selectedTruck.licensePlate}`
      );
      mutateScheduledData();

      // Recalculer l'itinéraire et les ETA pour le camion affecté
      await fetchRouteForSingleTruck(selectedTruck.id);
    } catch (error) {
      // Erreur : rollback des états et afficher l'erreur
      console.error("Erreur lors de l'affectation en masse:", error);
      setDeliveries(previousDeliveries);
      setTrucks(previousTrucks);
      setSelectedDeliveries(previousSelectedDeliveries);
      updateMapAfterReassignment();
    }
  };

  // Fonctions de filtrage
  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, searchQuery: e.target.value }));
  };

  const handleTruckFilterChange = (truckId) => {
    setFilters((prev) => {
      const currentFilters = prev.selectedTruckFilters;
      const isAlreadySelected = currentFilters.includes(truckId);

      if (isAlreadySelected) {
        // Retirer le camion de la sélection
        return {
          ...prev,
          selectedTruckFilters: currentFilters.filter((id) => id !== truckId),
        };
      } else {
        // Ajouter le camion à la sélection
        return {
          ...prev,
          selectedTruckFilters: [...currentFilters, truckId],
        };
      }
    });
  };

  const handleDateFilterChange = (date) => {
    setFilters((prev) => ({ ...prev, selectedDate: date }));
  };

  // Fonction pour gérer le changement de date avec le DatePicker
  const onChangeDate = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      handleDateFilterChange(formattedDate);
    } else {
      const today = moment().format('YYYY-MM-DD');
      handleDateFilterChange(today);
    }

    // Réinitialiser la carte et les KPI lors du changement de date
    setRouteData({});
    setSelectedMapTruck(null);
    setDeliveryCoordinates({});
    setKpiData({
      totalKilometers: 0,
      totalWeight: 0,
      totalTrucks: 0,
      totalDeliveries: 0,
      totalCost: 0,
      totalCO2: 0,
      co2PerDelivery: 0,
      totalMissions: 0,
      unassignedMissions: 0,
      missionsWithoutETA: 0,
    });

    // Réinitialiser la vue de la carte
    if (mapRef.current) {
      setTimeout(() => {
        try {
          mapRef.current.setView([48.8566, 2.3522], 10);
        } catch (error) {
          console.error(
            'Erreur lors de la réinitialisation de la carte:',
            error
          );
        }
      }, 100);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      searchQuery: '',
      selectedTruckFilters: [],
      selectedDate: moment().format('YYYY-MM-DD'),
    });
  };

  // Fonction pour désaffecter une étape d'un véhicule
  const handleUnassignStep = async (delivery) => {
    // Identifier le camion d'origine avant la désaffectation
    const originalTruckId =
      delivery.vehicleId ||
      trucks.find((t) => t.licensePlate === delivery.truck)?.id;

    // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
    const previousDeliveries = JSON.parse(JSON.stringify(deliveries));
    const previousTrucks = JSON.parse(JSON.stringify(trucks));

    try {
      // Optimistic update : modifier l'état local d'abord
      const updatedDeliveries = deliveries.map((d) => {
        if (d.id === delivery.id) {
          return {
            ...d,
            truck: 'Non affecté',
            vehicleId: null,
            tourId: null,
            order: 0,
          };
        }
        return d;
      });

      // Réorganiser les étapes restantes dans le même camion (côté frontend)
      const remainingDeliveriesInSameTruck = updatedDeliveries.filter(
        (d) => d.truck === delivery.truck && d.truck !== 'Non affecté'
      );

      if (remainingDeliveriesInSameTruck.length > 0) {
        // Trier par ordre actuel et réassigner séquentiellement
        remainingDeliveriesInSameTruck
          .sort((a, b) => a.order - b.order)
          .forEach((d, index) => {
            const deliveryIndex = updatedDeliveries.findIndex(
              (del) => del.id === d.id
            );
            if (deliveryIndex !== -1) {
              updatedDeliveries[deliveryIndex].order = index + 1;
            }
          });
      }

      const updatedTrucks = trucks.map((truck) => {
        const assignedDeliveries = updatedDeliveries.filter(
          (d) => d.truck === truck.licensePlate
        );

        const totalWeight = assignedDeliveries.reduce(
          (sum, d) => sum + parseFloat(d.weight || 0),
          0
        );

        const totalLinearMeters = assignedDeliveries.reduce(
          (sum, d) => sum + parseFloat(d.linearMeters || 0),
          0
        );

        return {
          ...truck,
          usedCapacity: parseFloat(totalWeight.toFixed(2)),
          usedLinearMeters: parseFloat(totalLinearMeters.toFixed(2)),
        };
      });

      // Mettre à jour les états (optimistic update)
      setDeliveries(updatedDeliveries);
      setTrucks(updatedTrucks);

      // Appel API pour synchroniser avec le backend
      // Note: Le backend gère automatiquement la réorganisation et la suppression de tournée si nécessaire
      await unassignStep(delivery.tourId, delivery.id);

      // Toast de succès seulement si tout s'est bien passé
      toast.success(`Étape #${delivery.id} retirée de la tournée`);
      mutateScheduledData();

      // Recalculer l'itinéraire et les ETA pour le camion d'origine s'il existait
      if (originalTruckId) {
        await fetchRouteForSingleTruck(originalTruckId);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la désaffectation:', error);

      // Rollback : restaurer l'état précédent
      setDeliveries(previousDeliveries);
      setTrucks(previousTrucks);

      toast.error(
        "Erreur lors de la désaffectation. L'état précédent a été restauré."
      );
    }
  };

  const handleDeleteUnassignedStep = async (delivery) => {
    // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
    const previousDeliveries = JSON.parse(JSON.stringify(deliveries));

    try {
      // Optimistic update : supprimer la livraison de l'état local d'abord
      const updatedDeliveries = deliveries.filter((d) => d.id !== delivery.id);

      // Mettre à jour l'état (optimistic update)
      setDeliveries(updatedDeliveries);

      // Appel API pour supprimer côté backend avec le vrai tour_planning_id
      await deleteUnassignedStep(delivery.tourPlanningId);

      // Toast de succès seulement si tout s'est bien passé
      toast.success(`Étape non affectée #${delivery.tourPlanningId} supprimée`);
      mutateScheduledData();
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);

      // Rollback : restaurer l'état précédent
      setDeliveries(previousDeliveries);

      toast.error(
        "Erreur lors de la suppression. L'état précédent a été restauré."
      );
    }
  };

  // Fonction pour désaffecter toutes les commandes d'un camion
  const handleUnassignAllTruckSteps = async (truck) => {
    const truckDeliveries = deliveries.filter(
      (d) => d.truck === truck.licensePlate && d.vehicleId === truck.id
    );

    if (truckDeliveries.length === 0) {
      toast.warning(`Le camion ${truck.key || truck.id} n'a aucune livraison à désaffecter`);
      return;
    }

    // Demander confirmation
    if (!window.confirm(
      `Êtes-vous sûr de vouloir désaffecter toutes les ${truckDeliveries.length} livraison(s) du camion ${truck.key || truck.id} ?`
    )) {
      return;
    }

    // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
    const previousDeliveries = JSON.parse(JSON.stringify(deliveries));
    const previousTrucks = JSON.parse(JSON.stringify(trucks));

    try {
      // Optimistic update : modifier l'état local d'abord
      const updatedDeliveries = deliveries.map((d) => {
        if (truckDeliveries.some((td) => td.id === d.id)) {
          return {
            ...d,
            truck: 'Non affecté',
            vehicleId: null,
            tourId: null,
            order: 0,
            etaTime: null,
          };
        }
        return d;
      });

      const updatedTrucks = trucks.map((t) => {
        if (t.id === truck.id) {
          return {
            ...t,
            usedCapacity: 0,
            usedLinearMeters: 0,
          };
        }
        return t;
      });

      // Mettre à jour les états (optimistic update)
      setDeliveries(updatedDeliveries);
      setTrucks(updatedTrucks);

      // Appel API pour chaque étape
      const unassignPromises = truckDeliveries.map((delivery) =>
        unassignStep(delivery.tourId, delivery.id)
      );

      await Promise.all(unassignPromises);

      // Toast de succès
      toast.success(`${truckDeliveries.length} livraison(s) retirée(s) du camion ${truck.key || truck.id}`);
      
      // Nettoyer les données de route pour ce camion
      setRouteData((prevRouteData) => {
        const { [truck.id]: _removedRoute, ...remainingRoutes } = prevRouteData;
        return remainingRoutes;
      });

      // Rafraîchir les données
      mutateScheduledData();
    } catch (error) {
      console.error('❌ Erreur lors de la désaffectation en masse:', error);

      // Rollback : restaurer l'état précédent
      setDeliveries(previousDeliveries);
      setTrucks(previousTrucks);

      toast.error(
        "Erreur lors de la désaffectation. L'état précédent a été restauré."
      );
    }
  };

  // Fonction pour retourner les données (filtrage maintenant côté API)
  const getFilteredData = React.useCallback(() => {
    return { filteredTrucks: trucks, filteredDeliveries: deliveries };
  }, [trucks, deliveries]);

  // Fonctions de drag and drop
  const handleDragStart = (e, delivery, truckId) => {
    setDraggedDelivery({ delivery, truckId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedDelivery(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleRowDrop = async (e, dropIndex, targetTruckId) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedDelivery) return;

    const { delivery: draggedDeliveryItem, truckId: sourceTruckId } =
      draggedDelivery;

    // Si on dépose sur le même camion
    if (sourceTruckId === targetTruckId) {
      // Réorganiser les livraisons dans le même camion
      const truckDeliveries = deliveries
        .filter(
          (d) =>
            d.truck === trucks.find((t) => t.id === targetTruckId)?.licensePlate
        )
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const draggedIndex = truckDeliveries.findIndex(
        (d) => d.id === draggedDeliveryItem.id
      );

      if (draggedIndex === -1 || draggedIndex === dropIndex) return;

      // Créer le nouvel ordre
      const newOrder = [...truckDeliveries];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, removed);

      // Sauvegarder l'état précédent pour le rollback potentiel (deep copy)
      const previousDeliveries = JSON.parse(JSON.stringify(deliveries));

      // Mettre à jour les order (optimistic update)
      const updatedDeliveries = deliveries.map((delivery) => {
        const newOrderIndex = newOrder.findIndex((no) => no.id === delivery.id);
        if (newOrderIndex !== -1) {
          return { ...delivery, order: newOrderIndex + 1 };
        }
        return delivery;
      });

      setDeliveries(updatedDeliveries);

      try {
        const stepIdSource = draggedDeliveryItem.id;
        const stepIdTarget = truckDeliveries[dropIndex].id;
        const tourId = draggedDeliveryItem.tourId;

        console.log('removed', removed);

        await reorderSteps(stepIdSource, stepIdTarget, tourId);
        mutateScheduledData();

        // Recalculer l'itinéraire et les ETA pour le camion affecté
        await fetchRouteForSingleTruck(targetTruckId);
      } catch (error) {
        console.error(
          '❌ Erreur lors de la synchronisation avec le backend:',
          error
        );

        // Rollback : restaurer l'état précédent
        setDeliveries(previousDeliveries);

        toast.error(
          "Erreur lors de la réorganisation. L'ordre précédent a été restauré."
        );
      }
    }

    setDraggedDelivery(null);
  };

  return (
    <>
      {/* Overlay de chargement pour l'optimisation complète */}
      {isFullOptimizationRunning && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white'
          }}
        >
          <div className="spinner-border" role="status" style={{ width: '4rem', height: '4rem', marginBottom: '20px' }}>
            <span className="visually-hidden">Chargement...</span>
          </div>
          <h3 style={{ marginBottom: '10px' }}>Optimisation complète en cours...</h3>
          <p style={{ fontSize: '16px', textAlign: 'center', maxWidth: '500px' }}>
            Affectation des livraisons, optimisation des séquences et calcul des itinéraires.
            <br />
            Veuillez patienter...
          </p>
        </div>
      )}

      <GenericPageContainer
        title={'Ordonnancement'}
        page={'Ordonnancement'}
        subtitle={
          'Ordonnancez vos livraisons par camion de la manière que vous le souhaitez'
        }
        filters={''}
      >
        <div className="import-results">
          {/* KPI Bar */}
          <div className="kpi-bar">
            <div className="kpi-container">
              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">
                    {kpiData.totalMissions} missions
                  </div>
                  <div className="kpi-label">
                    {kpiData.unassignedMissions > 0 && (
                      <span className="kpi-alert">
                        {kpiData.unassignedMissions} non affectées
                      </span>
                    )}
                    {kpiData.missionsWithoutETA > 0 && (
                      <span className="kpi-alert">
                        {kpiData.missionsWithoutETA} sans ETA
                      </span>
                    )}
                    {kpiData.unassignedMissions === 0 &&
                      kpiData.missionsWithoutETA === 0 && (
                        <span>
                          Toutes les missions sont affectées et ont un ETA
                        </span>
                      )}
                  </div>
                </div>
              </div>

              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">
                    {kpiData.totalKilometers.toFixed(2)} km
                  </div>
                  <div className="kpi-label">Distance totale</div>
                </div>
              </div>

              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">
                    {kpiData.totalWeight.toFixed(2)} kg
                  </div>
                  <div className="kpi-label">Poids total</div>
                </div>
              </div>

              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">{kpiData.totalTrucks} camions</div>
                  <div className="kpi-label">
                    {kpiData.totalDeliveries} livraisons
                  </div>
                </div>
              </div>

              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">
                    {kpiData.totalCost.toFixed(2)} €
                  </div>
                  <div className="kpi-label">Coût total</div>
                </div>
              </div>

              <div className="kpi-item">
                <div className="kpi-content">
                  <div className="kpi-value">
                    {kpiData.totalCO2.toFixed(2)} kg
                  </div>
                  <div className="kpi-label">
                    CO2 total ({(kpiData.co2PerDelivery || 0).toFixed(2)}{' '}
                    kg/livraison)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons row */}
          <div className="action-buttons-row">
            {/* Filtres */}
            <div className="filters-section">
              {/* Barre de recherche */}
              <div className="search-container">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Rechercher par camion, client, ID..."
                    value={filters.searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '200px' }}
                  />
                </div>
              </div>

              {/* Filtre par date avec composant Agrivitech */}
              <div className="date-filter-container tw-relative">
                <ReactDatePicker
                  selected={
                    filters.selectedDate ? new Date(filters.selectedDate) : null
                  }
                  onChange={onChangeDate}
                  customInput={<CustomDatePickerDashboard />}
                  locale="fr"
                  dateFormat="dd MMM yyyy"
                />
              </div>

              {/* Filtres par camion */}
              <div className="filter-dropdown">
                <div className="dropdown">
                  <button
                    className={`btn btn-sm ${filters.selectedTruckFilters.length > 0 ? 'btn-primary' : 'btn-outline-secondary'} dropdown-toggle`}
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <FontAwesomeIcon icon={faTruck} />
                    {filters.selectedTruckFilters.length === 0
                      ? 'Tous les camions'
                      : filters.selectedTruckFilters.length === 1
                        ? `Camion ${trucks.find((t) => t.id === filters.selectedTruckFilters[0])?.key || filters.selectedTruckFilters[0]}`
                        : `${filters.selectedTruckFilters.length} camions sélectionnés`}
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            selectedTruckFilters: [],
                          }))
                        }
                      >
                        <FontAwesomeIcon icon={faEraser} className="me-2" />
                        Tous les camions
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            selectedTruckFilters: trucks.map((t) => t.id),
                          }))
                        }
                      >
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Sélectionner tous les camions
                      </button>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    {trucks.map((truck) => (
                      <li key={truck.id}>
                        <button
                          className={`dropdown-item d-flex align-items-center ${filters.selectedTruckFilters.includes(truck.id) ? 'active' : ''}`}
                          onClick={() => handleTruckFilterChange(truck.id)}
                        >
                          <input
                            type="checkbox"
                            checked={filters.selectedTruckFilters.includes(
                              truck.id
                            )}
                            onChange={() => {}} // Géré par le onClick du bouton
                            className="me-2"
                            style={{ pointerEvents: 'none' }}
                          />
                          <span
                            className="truck-color-dot me-2"
                            style={{
                              backgroundColor: getTruckColor(truck.id),
                            }}
                          ></span>
                          <span>Camion {truck.key || truck.id}</span>
                          <span className="ms-auto text-muted small">
                            {
                              deliveries.filter(
                                (d) => d.truck === truck.licensePlate
                              ).length
                            }{' '}
                            livraisons
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bouton pour vider les filtres */}
              {(filters.searchQuery ||
                filters.selectedTruckFilters.length > 0 ||
                (filters.selectedDate &&
                  filters.selectedDate !== moment().format('YYYY-MM-DD'))) && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={clearAllFilters}
                  title="Vider tous les filtres"
                >
                  <FontAwesomeIcon icon={faEraser} /> Vider
                </button>
              )}
            </div>

            <div className="action-buttons-right">
              <button
                className="btn btn-outline-info mr-2"
                onClick={() => setShowModalConfig(true)}
                title="Configuration de l'ordonnancement"
              >
                <FontAwesomeIcon icon={faCog} /> Configuration
              </button>
              <button
                className="btn btn-outline-success"
                disabled={isFullOptimizationRunning}
                onClick={handleFullOptimization}
              >
                {isFullOptimizationRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span> Optimisation en cours...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} /> Optimiser toutes les livraisons
                  </>
                )}
              </button>
              <button
                className="btn btn-primary"
                disabled={fetchingRoutes}
                onClick={
                  selectedMapTruck
                    ? () => fetchRouteForSingleTruck(selectedMapTruck)
                    : fetchRouteForTrucks
                }
              >
                <FontAwesomeIcon icon={faRoute} />
                {fetchingRoutes
                  ? 'Calcul en cours...'
                  : 'Calculer les itinéraires'}
              </button>
              {/* {config.fleet_optimization && (
                <button
                className="btn btn-success ml-2"
                title="Optimiser l'ordonnancement des livraisons"
              >
                <FontAwesomeIcon icon={faLayerGroup} /> Optimiser
                l'ordonnancement
              </button>)} */}
            </div>
          </div>

          <div className="two-column-layout">
            {/* Colonne de gauche : Livraisons */}
            <div className="left-column">
              {/* Grouper les livraisons par camion */}
              <div className="deliveries-by-truck">
                {/* Livraisons non affectées */}
                {(() => {
                  const { filteredDeliveries } = getFilteredData();
                  const unassignedDeliveries = filteredDeliveries.filter(
                    (d) =>
                      !d.vehicleId && (d.truck === 'Non affecté' || !d.truck)
                  );

                  if (unassignedDeliveries.length === 0) return null;

                  return (
                    <div className="truck-delivery-group unassigned-deliveries">
                      <div
                        className="truck-group-header"
                        style={{ backgroundColor: '#f0f0f0' }}
                      >
                        <div className="truck-group-title">
                          <span
                            className="truck-color-dot"
                            style={{ backgroundColor: '#777777' }}
                          ></span>
                          <h4>Livraisons non affectées</h4>
                        </div>

                        <div className="">
                          <span className="delivery-group-count ">
                            {unassignedDeliveries.length} livraison(s) -{' '}
                            {unassignedDeliveries
                              .reduce((sum, d) => sum + parseFloat(d.weight), 0)
                              .toFixed(2)}{' '}
                            kg
                          </span>
                          {selectedDeliveries.length > 0 && (
                            <button
                              className="btn btn-primary btn-sm ml-2"
                              onClick={openBulkAssignModal}
                            >
                              <FontAwesomeIcon icon={faLayerGroup} /> Réaffecter{' '}
                              {selectedDeliveries.length} livraison(s)
                            </button>
                          )}
                        </div>
                      </div>

                      <div
                        id="unassigned-deliveries-table"
                        className="table-responsive"
                      >
                        <Table
                          className="deliveries-table"
                          responsive
                          borderless
                          hover
                        >
                          <thead>
                            <tr>
                              <th>
                                <div className="checkbox-container">
                                  <input
                                    type="checkbox"
                                    checked={unassignedDeliveries.every((d) =>
                                      selectedDeliveries.some(
                                        (sd) => sd.id === d.id
                                      )
                                    )}
                                    onChange={() => {
                                      if (
                                        unassignedDeliveries.every((d) =>
                                          selectedDeliveries.some(
                                            (sd) => sd.id === d.id
                                          )
                                        )
                                      ) {
                                        setSelectedDeliveries(
                                          selectedDeliveries.filter(
                                            (d) =>
                                              !unassignedDeliveries.some(
                                                (ud) => ud.id === d.id
                                              )
                                          )
                                        );
                                      } else {
                                        const newSelectedDeliveries = [
                                          ...selectedDeliveries,
                                        ];
                                        unassignedDeliveries.forEach((d) => {
                                          if (
                                            !newSelectedDeliveries.some(
                                              (sd) => sd.id === d.id
                                            )
                                          ) {
                                            newSelectedDeliveries.push(d);
                                          }
                                        });
                                        setSelectedDeliveries(
                                          newSelectedDeliveries
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              </th>
                              <th title="Glisser-déposer pour réorganiser">
                                <FontAwesomeIcon
                                  icon={faGripLines}
                                  style={{ opacity: 0.4 }}
                                />
                              </th>
                              <th>CLIENT</th>
                              <th>ETA</th>
                              <th>CODE POSTAL</th>
                              <th>POIDS</th>
                              <th>ACTIONS</th>
                              <th>ORDRE</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unassignedDeliveries.map((delivery) => (
                              <tr
                                key={delivery.id}
                                className={
                                  selectedDeliveries.some(
                                    (d) => d.id === delivery.id
                                  )
                                    ? 'selected-row'
                                    : ''
                                }
                                onClick={() =>
                                  toggleDeliverySelection(delivery)
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <div className="checkbox-container">
                                    <input
                                      type="checkbox"
                                      checked={selectedDeliveries.some(
                                        (d) => d.id === delivery.id
                                      )}
                                      onChange={() =>
                                        toggleDeliverySelection(delivery)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </td>
                                <td> </td>
                                <td>{delivery.client}</td>
                                <td>
                                  {delivery.etaTime ? (
                                    <span
                                      style={{
                                        color: '#079992',
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {delivery.etaTime}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        color: '#6c757d',
                                        fontSize: '12px',
                                      }}
                                    >
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>{delivery.zipcode}</td>
                                <td>{delivery.weight} kg</td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      className="btn btn-sm btn-outline-primary action-btn"
                                      title="Assigner à un camion"
                                      onClick={() => openAssignModal(delivery)}
                                    >
                                      <FontAwesomeIcon icon={faTruck} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger action-btn"
                                      title="Supprimer cette étape non affectée"
                                      onClick={() =>
                                        handleDeleteUnassignedStep(delivery)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </td>
                                <td>-</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  );
                })()}

                {/* Livraisons par camion */}
                {getFilteredData().filteredTrucks.map((truck) => {
                  const { filteredDeliveries } = getFilteredData();
                  const truckDeliveries = filteredDeliveries
                    .filter((delivery) => delivery.truck === truck.licensePlate)
                    .sort((a, b) => {
                      const orderA = a.order || 0;
                      const orderB = b.order || 0;
                      return orderA - orderB;
                    });

                  if (truckDeliveries.length === 0) return null;

                  const routeInfo =
                    routeData[truck.id]?.features?.[0]?.properties?.summary;
                  const hasRouteInfo = !!routeInfo;

                  return (
                    <div key={truck.id} className="truck-delivery-group">
                      <div
                        className="truck-group-header"
                        style={{ 
                          backgroundColor: '#f9f9f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 15px',
                          borderRadius: '5px',
                          marginBottom: '10px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div className="truck-group-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              className="truck-color-dot"
                              style={{
                                backgroundColor: getTruckColor(truck.id),
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                display: 'inline-block'
                              }}
                            ></span>
                            <h4 style={{ margin: 0, fontSize: '16px' }}>Camion {truck.key || truck.id}</h4>
                          </div>
                          
                          <div className="truck-group-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                             <button
                               className="btn btn-sm"
                               onClick={() => optimizeVehicleSequence(truck)}
                               disabled={isVehicleOptimizing(truck.id) || truckDeliveries.length < 2}
                               title={truckDeliveries.length < 2 ? "Le camion doit avoir au moins 2 étapes pour être optimisé" : "Optimiser l'ordre des étapes de ce camion"}
                               style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                             >
                               {isVehicleOptimizing(truck.id) ? (
                                 <>
                                   <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                   <span>Optimisation...</span>
                                 </>
                               ) : (
                                 <>
                                   <FontAwesomeIcon icon={faMagicWandSparkles} className="color-primary" />
                                   <span>Optimiser</span>
                                 </>
                               )}
                             </button>
                             <button
                               className="btn btn-sm btn-outline-danger"
                               onClick={() => handleUnassignAllTruckSteps(truck)}
                               disabled={truckDeliveries.length === 0 || unassignLoading}
                               title="Désaffecter toutes les livraisons de ce camion"
                               style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                             >
                               <FontAwesomeIcon icon={faTrash} />
                               <span>Vider</span>
                             </button>
                             {!isVehicleOptimizing(truck.id) && vehicleOptimizationStats[truck.id]?.totalTimeReduction > 0 && (
                               <span
                                 className="badge bg-success"
                                 style={{
                                   marginLeft: '8px',
                                   fontSize: '12px',
                                   display: 'inline-flex',
                                   alignItems: 'center',
                                   gap: '4px',
                                   whiteSpace: 'nowrap',
                                 }}
                               >
                                 <FontAwesomeIcon icon={faClock} />
                                 {Math.round(vehicleOptimizationStats[truck.id].totalTimeReduction)} min économisées
                               </span>
                             )}
                          </div>
                        </div>

                        <div className="delivery-group-count" style={{ fontSize: '14px', color: '#666' }}>
                          {truckDeliveries.length} livraison(s) -{' '}
                          {(() => {
                            const totalWeight = truckDeliveries.reduce(
                              (sum, d) => sum + parseFloat(d.weight),
                              0
                            );
                            return (
                              <span
                                className={
                                  (totalWeight / truck.capacity) * 100 >= 80
                                    ? totalWeight > truck.capacity
                                      ? 'tour-distance-badge-danger'
                                      : 'tour-distance-badge-warning'
                                    : ''
                                }
                              >
                                {totalWeight.toFixed(2)} kg
                              </span>
                            );
                          })()}{' '}
                          -{' '}
                          {hasRouteInfo ? (
                            <span
                              className={
                                routeInfo.distance / 1000 > generalConfig.max_km
                                  ? 'tour-distance-badge-danger'
                                  : ''
                              }
                            >
                              {(routeInfo.distance / 1000).toFixed(2)} km
                            </span>
                          ) : truck.routeError === true ? (
                            <span
                              className="route-error-badge"
                              title={
                                truck.routeErrorMessage ||
                                "Erreur de calcul d'itinéraire"
                              }
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                              Erreur d'itinéraire
                            </span>
                          ) : (
                            <span>Aucun itinéraire</span>
                          )}
                        </div>
                      </div>

                      <div className="table-responsive">
                        <Table
                          className="deliveries-table"
                          responsive
                          borderless
                          hover
                        >
                          <thead>
                            <tr>
                              <th>
                                <div className="checkbox-container">
                                  <input
                                    type="checkbox"
                                    checked={truckDeliveries.every((d) =>
                                      selectedDeliveries.some(
                                        (sd) => sd.id === d.id
                                      )
                                    )}
                                    onChange={() => {
                                      if (
                                        truckDeliveries.every((d) =>
                                          selectedDeliveries.some(
                                            (sd) => sd.id === d.id
                                          )
                                        )
                                      ) {
                                        setSelectedDeliveries(
                                          selectedDeliveries.filter(
                                            (d) =>
                                              !truckDeliveries.some(
                                                (td) => td.id === d.id
                                              )
                                          )
                                        );
                                      } else {
                                        const newSelected = [
                                          ...selectedDeliveries,
                                        ];
                                        truckDeliveries.forEach((d) => {
                                          if (
                                            !newSelected.some(
                                              (sd) => sd.id === d.id
                                            )
                                          ) {
                                            newSelected.push(d);
                                          }
                                        });
                                        setSelectedDeliveries(newSelected);
                                      }
                                    }}
                                  />
                                </div>
                              </th>
                              <th title="Glisser-déposer pour réorganiser">
                                <FontAwesomeIcon
                                  icon={faGripLines}
                                  style={{ opacity: 0.4 }}
                                />
                              </th>
                              <th>CLIENT</th>
                              <th>ETA</th>
                              <th>CODE POSTAL</th>
                              <th>POIDS</th>
                              <th>ACTIONS</th>
                              <th>ORDRE</th>
                            </tr>
                          </thead>
                          <tbody>
                            {truckDeliveries.map((delivery, index) => (
                              <tr
                                key={delivery.id}
                                className={`
                                    ${selectedDeliveries.some((d) => d.id === delivery.id) ? 'selected-row' : ''}
                                    ${draggedDelivery?.delivery.id === delivery.id ? 'dragging' : ''}
                                    ${dragOverIndex === index ? 'drag-over' : ''}
                                    ${routeErrorDeliveries[delivery.id] ? 'route-error-row' : ''}
                                    draggable-row
                                  `}
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, delivery, truck.id)
                                }
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) =>
                                  handleRowDrop(e, index, truck.id)
                                }
                                onClick={() =>
                                  toggleDeliverySelection(delivery)
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <div className="checkbox-container">
                                    <input
                                      type="checkbox"
                                      checked={selectedDeliveries.some(
                                        (d) => d.id === delivery.id
                                      )}
                                      onChange={() =>
                                        toggleDeliverySelection(delivery)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <div
                                    className="drag-handle"
                                    title="Glisser pour réorganiser"
                                  >
                                    <FontAwesomeIcon icon={faGripLines} />
                                  </div>
                                </td>

                                <td>{delivery.client}</td>
                                <td>
                                  {delivery.etaTime ? (
                                    <span
                                      className="weight-badge"
                                      style={{
                                        color: '#079992',
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {delivery.etaTime}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        color: '#6c757d',
                                        fontSize: '12px',
                                      }}
                                    >
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>{delivery.zipcode}</td>
                                <td>
                                  <span className="weight-badge">
                                    {delivery.weight.toFixed(2)} kg
                                  </span>
                                </td>
                                <td>
                                  <div className="actions-container">
                                    <button
                                      className="btn btn-sm btn-outline-primary action-btn"
                                      title="Réassigner à un autre camion"
                                      onClick={() => openAssignModal(delivery)}
                                    >
                                      <FontAwesomeIcon icon={faTruck} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-secondary action-btn"
                                      title="Retirer du camion"
                                      onClick={() =>
                                        handleUnassignStep(delivery)
                                      }
                                      disabled={unassignLoading}
                                    >
                                      <div
                                        style={{
                                          position: 'relative',
                                          display: 'inline-block',
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faTruck} />
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '0',
                                            right: '0',
                                            height: '1px',
                                            backgroundColor: '#6c757d',
                                            transform:
                                              'translateY(-50%) rotate(-45deg)',
                                            transformOrigin: 'center',
                                          }}
                                        ></div>
                                      </div>
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className="order-badge"
                                    style={{
                                      backgroundColor: getTruckColor(truck.id),
                                      color: 'white',
                                      padding: '2px 6px',
                                      borderRadius: '50%',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      minWidth: '20px',
                                      textAlign: 'center',
                                      display: 'inline-block',
                                    }}
                                  >
                                    {delivery.order || '-'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Colonne de droite : Carte */}
            <div className="right-column">
              <div className="map-column">
                <div
                  className="map-container-column"
                  style={{ height: '80vh', minHeight: '400px' }}
                >
                  <MapContainer
                    center={[48.866669, 2.33333]}
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}
                    ref={mapRef}
                    whenCreated={(map) => {
                      mapRef.current = map;
                      console.log('🗺️ Carte créée avec succès', {
                        containerSize: map
                          .getContainer()
                          .getBoundingClientRect(),
                        mapSize: map.getSize(),
                      });
                      setTimeout(() => {
                        map.invalidateSize(true);
                        console.log('🗺️ Taille de carte invalidée', {
                          newSize: map.getSize(),
                        });
                      }, 100);
                    }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    />

                    {/* Afficher les polylines d'itinéraires */}
                    {Object.keys(routeData).map((truckId) => {
                      // Si un camion est sélectionné et que ce n'est pas celui-ci, on saute ce camion
                      if (
                        selectedMapTruck !== null &&
                        String(selectedMapTruck) !== String(truckId)
                      ) {
                        return null;
                      }

                      const route = routeData[truckId];
                      if (
                        !route ||
                        !route.features ||
                        !route.features[0] ||
                        !route.features[0].geometry
                      ) {
                        return null;
                      }

                      const coordinates =
                        route.features[0].geometry.coordinates.map((coord) => [
                          coord[1],
                          coord[0],
                        ]);
                      const truckColor = getTruckColor(truckId);

                      return (
                        <Polyline
                          key={`route-${truckId}`}
                          positions={coordinates}
                          color={truckColor}
                          weight={4}
                          opacity={0.7}
                        />
                      );
                    })}

                    {/* MarkerClusterGroup pour tous les marqueurs */}
                    <MarkerClusterGroup
                      chunkedLoading
                      // iconCreateFunction={createClusterCustomIcon}
                      maxClusterRadius={40}
                      spiderfyOnMaxZoom={true}
                      showCoverageOnHover={false}
                      zoomToBoundsOnClick={true}
                      removeOutsideVisibleBounds={true}
                    >
                      {/* Afficher les points de livraison */}
                      {Object.keys(deliveryCoordinates).length > 0 &&
                        trucks.map((truck) => {
                          // Si un camion est sélectionné et que ce n'est pas celui-ci, on saute ce camion
                          if (
                            selectedMapTruck !== null &&
                            selectedMapTruck !== truck.id
                          ) {
                            return null;
                          }

                          const truckColor = getTruckColor(truck.id);

                          // Trouver toutes les livraisons assignées à ce camion et les trier par ordre
                          const truckDeliveries =
                            getOrderedDeliveriesForTruck(truck);

                          return (
                            <React.Fragment key={truck.id}>
                              {truckDeliveries.map((delivery, index) => {
                                const coords = deliveryCoordinates[delivery.id];
                                if (!coords) return null;

                                // Utiliser l'ordre de l'API ou l'index comme fallback
                                const orderNumber =
                                  delivery.vroom_step_order || index + 1;

                                // Créer un marqueur personnalisé avec le numéro d'ordre
                                const customIcon = L.divIcon({
                                  html: `
                                  <div style="
                                    background-color: ${truckColor};
                                    border: 2px solid white;
                                    border-radius: 50%;
                                    width: 24px;
                                    height: 24px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    color: white;
                                    font-weight: bold;
                                    font-size: 12px;
                                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                                  ">
                                    ${orderNumber}
                                  </div>
                                `,
                                  className: 'custom-delivery-marker',
                                  iconSize: [24, 24],
                                  iconAnchor: [12, 12],
                                });

                                return (
                                  <Marker
                                    key={delivery.id}
                                    position={[coords.lat, coords.lng]}
                                    icon={customIcon}
                                  >
                                    <Popup>
                                      <div className="map-popup-content">
                                        <h6
                                          style={{
                                            color: truckColor,
                                            marginBottom: '8px',
                                          }}
                                        >
                                          Livraison #{delivery.id} - Étape{' '}
                                          {orderNumber}
                                        </h6>
                                        <div
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto 1fr',
                                            gap: '4px',
                                            fontSize: '12px',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          <span>
                                            <strong>Ordre:</strong>
                                          </span>
                                          <span
                                            style={{
                                              color: truckColor,
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            Étape {orderNumber}
                                          </span>
                                          {delivery.etaTime && (
                                            <>
                                              <span>
                                                <strong>ETA:</strong>
                                              </span>
                                              <span
                                                style={{
                                                  color: '#28a745',
                                                  fontWeight: 'bold',
                                                }}
                                              >
                                                {delivery.etaTime}
                                              </span>
                                            </>
                                          )}
                                          <span>
                                            <strong>Client:</strong>
                                          </span>
                                          <span>
                                            {delivery.clientName ||
                                              delivery.client}
                                          </span>
                                          <span>
                                            <strong>Adresse:</strong>
                                          </span>
                                          <span>{delivery.street}</span>
                                          <span>
                                            <strong>Ville:</strong>
                                          </span>
                                          <span>
                                            {delivery.zipcode} {delivery.city}
                                          </span>
                                          <span>
                                            <strong>Poids:</strong>
                                          </span>
                                          <span>
                                            {delivery.weight.toFixed(2)} kg
                                          </span>
                                          <span>
                                            <strong>Camion:</strong>
                                          </span>
                                          <span
                                            style={{
                                              color: truckColor,
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            {truck.key || truck.id}
                                          </span>
                                        </div>
                                        <div
                                          style={{
                                            display: 'flex',
                                            gap: '8px',
                                            marginTop: '8px',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <button
                                            className="btn btn-sm btn-outline-primary"
                                            style={{
                                              fontSize: '10px',
                                              padding: '2px 6px',
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openAssignModal(delivery);
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={faTruck}
                                              style={{ marginRight: '4px' }}
                                            />
                                            Réassigner
                                          </button>
                                          <button
                                            className="btn btn-sm btn-outline-secondary"
                                            style={{
                                              fontSize: '10px',
                                              padding: '2px 6px',
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Retirer la livraison du camion
                                              const updatedDeliveries =
                                                deliveries.map((d) => {
                                                  if (d.id === delivery.id) {
                                                    return {
                                                      ...d,
                                                      truck: 'Non affecté',
                                                      vehicleId: null,
                                                    };
                                                  }
                                                  return d;
                                                });

                                              // Recalculer les charges des camions
                                              const updatedTrucks = trucks.map(
                                                (t) => {
                                                  const assignedDeliveries =
                                                    updatedDeliveries.filter(
                                                      (d) =>
                                                        d.vehicleId === t.id ||
                                                        d.truck ===
                                                          t.licensePlate ||
                                                        d.truck === t.key
                                                    );

                                                  const totalWeight =
                                                    assignedDeliveries.reduce(
                                                      (sum, d) =>
                                                        sum +
                                                        parseFloat(d.weight),
                                                      0
                                                    );

                                                  const totalLinearMeters =
                                                    assignedDeliveries.reduce(
                                                      (sum, d) =>
                                                        sum +
                                                        parseFloat(
                                                          d.linearMeters || 0
                                                        ),
                                                      0
                                                    );

                                                  return {
                                                    ...t,
                                                    usedCapacity: parseFloat(
                                                      totalWeight.toFixed(2)
                                                    ),
                                                    usedLinearMeters:
                                                      parseFloat(
                                                        totalLinearMeters.toFixed(
                                                          2
                                                        )
                                                      ),
                                                  };
                                                }
                                              );

                                              setDeliveries(updatedDeliveries);
                                              setTrucks(updatedTrucks);
                                              toast.info(
                                                `Commande #${delivery.id} retirée du camion ${delivery.truck}`
                                              );
                                              // Mettre à jour la carte
                                              updateMapAfterReassignment();
                                            }}
                                          >
                                            <div
                                              style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                                marginRight: '4px',
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faTruck} />
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: '50%',
                                                  left: '0',
                                                  right: '0',
                                                  height: '1px',
                                                  backgroundColor: '#6c757d',
                                                  transform:
                                                    'translateY(-50%) rotate(-45deg)',
                                                  transformOrigin: 'center',
                                                }}
                                              ></div>
                                            </div>
                                            Retirer
                                          </button>
                                        </div>
                                      </div>
                                    </Popup>
                                  </Marker>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}

                      {/* Afficher les livraisons non affectées en gris clair */}
                      {(() => {
                        const { filteredDeliveries } = getFilteredData();
                        const unassignedDeliveries = filteredDeliveries.filter(
                          (d) =>
                            !d.vehicleId &&
                            (d.truck === 'Non affecté' || !d.truck)
                        );

                        return unassignedDeliveries.map((delivery) => {
                          const coords = deliveryCoordinates[delivery.id];
                          if (!coords) return null;

                          // Créer un marqueur gris clair pour les livraisons non affectées
                          const unassignedIcon = L.divIcon({
                            html: `
                              <div style="
                                background-color: #d3d3d3;
                                border: 2px solid white;
                                border-radius: 50%;
                                width: 20px;
                                height: 20px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: #666666;
                                font-weight: bold;
                                font-size: 10px;
                                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                              ">
                                ?
                              </div>
                            `,
                            className: 'custom-unassigned-marker',
                            iconSize: [20, 20],
                            iconAnchor: [10, 10],
                          });

                          return (
                            <Marker
                              key={`unassigned-${delivery.id}`}
                              position={[coords.lat, coords.lng]}
                              icon={unassignedIcon}
                            >
                              <Popup>
                                <div className="map-popup-content">
                                  <h6
                                    style={{
                                      color: '#666666',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    Livraison #{delivery.id} - Non affectée
                                  </h6>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'auto 1fr',
                                      gap: '4px',
                                      fontSize: '12px',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    <span>
                                      <strong>Statut:</strong>
                                    </span>
                                    <span
                                      style={{
                                        color: '#d3d3d3',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      Non affectée
                                    </span>
                                    <span>
                                      <strong>Client:</strong>
                                    </span>
                                    <span>
                                      {delivery.clientName || delivery.client}
                                    </span>
                                    <span>
                                      <strong>Adresse:</strong>
                                    </span>
                                    <span>{delivery.street}</span>
                                    <span>
                                      <strong>Ville:</strong>
                                    </span>
                                    <span>
                                      {delivery.zipcode} {delivery.city}
                                    </span>
                                    <span>
                                      <strong>Poids:</strong>
                                    </span>
                                    <span>{delivery.weight.toFixed(2)} kg</span>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '8px',
                                      marginTop: '8px',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <button
                                      className="btn btn-sm btn-primary"
                                      style={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAssignModal(delivery);
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faTruck}
                                        style={{ marginRight: '4px' }}
                                      />
                                      Assigner
                                    </button>
                                  </div>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        });
                      })()}
                    </MarkerClusterGroup>

                    {/* Afficher les factories sur la carte (en dehors du cluster) */}
                    {factories.length > 0 &&
                      factories.map((factory) => {
                        const factoryLat = parseFloat(factory.latitude);
                        const factoryLng = parseFloat(factory.longitude);

                        if (!isNaN(factoryLat) && !isNaN(factoryLng)) {
                          return (
                            <CircleMarker
                              key={`factory-${factory.id}`}
                              center={[factoryLat, factoryLng]}
                              radius={10}
                              pathOptions={{
                                fillColor: '#ff8800',
                                color: '#ffffff',
                                weight: 2,
                                fillOpacity: 0.9,
                              }}
                            >
                              <Popup>
                                <div className="factory-popup">
                                  <h6
                                    style={{
                                      color: '#ff8800',
                                      marginBottom: '8px',
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faIndustry}
                                      style={{ marginRight: '6px' }}
                                    />
                                    {factory.name || 'Factory/Depot'}
                                  </h6>
                                  <div>
                                    Ceci est le point de départ et d'arrivée des
                                    tournées
                                  </div>
                                </div>
                              </Popup>
                            </CircleMarker>
                          );
                        }
                        return null;
                      })}

                    {/* Afficher le point de départ/arrivée spécifique à chaque camion si défini (en dehors du cluster) */}
                    {selectedMapTruck &&
                      trucks.map((truck) => {
                        if (
                          truck.id !== selectedMapTruck ||
                          !truck.factoryLatitude ||
                          !truck.factoryLongitude
                        ) {
                          return null;
                        }

                        return (
                          <CircleMarker
                            key={`truck-factory-${truck.id}`}
                            center={[
                              truck.factoryLatitude,
                              truck.factoryLongitude,
                            ]}
                            radius={12}
                            pathOptions={{
                              fillColor: '#ff8800',
                              color: getTruckColor(truck.id),
                              weight: 3,
                              fillOpacity: 0.9,
                            }}
                          >
                            <Popup>
                              <div className="factory-popup">
                                <h6
                                  style={{
                                    color: getTruckColor(truck.id),
                                    marginBottom: '8px',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faIndustry}
                                    style={{ marginRight: '6px' }}
                                  />
                                  Point de départ/arrivée - Camion{' '}
                                  {truck.key || truck.id}
                                </h6>
                              </div>
                            </Popup>
                          </CircleMarker>
                        );
                      })}
                  </MapContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Modale d'assignation unifiée */}
          <Modal show={showAssignModal} onHide={closeAssignModal} size="lg">
            <Modal.Body>
              <div className="row">
                {/* Colonne de gauche - Détails de la livraison */}
                <div className="col-md-5">
                  {selectedDelivery ? (
                    <div className="delivery-info">
                      <h5 className="modal-section-title">
                        Informations de la commande
                      </h5>
                      <div className="delivery-details">
                        <div className="row">
                          <div className="col-md-6">
                            {selectedDelivery.etaTime && (
                              <div className="detail-item">
                                <span className="detail-label">ETA</span>
                                <span
                                  className="detail-value"
                                  style={{
                                    color: '#28a745',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {selectedDelivery.etaTime}
                                </span>
                              </div>
                            )}

                            <div className="detail-item">
                              <span className="detail-label">Client</span>
                              <span className="detail-value">
                                {selectedDelivery.clientName ||
                                  selectedDelivery.client}
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">Adresse</span>
                              <span className="detail-value">
                                {selectedDelivery.street}
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">Code postal</span>
                              <span className="detail-value">
                                {selectedDelivery.zipcode}
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">Ville</span>
                              <span className="detail-value">
                                {selectedDelivery.city}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="detail-item">
                              <span className="detail-label">Produits</span>
                              <span className="detail-value">
                                {selectedDelivery.products}
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">Poids</span>
                              <span className="detail-value">
                                {selectedDelivery.weight.toFixed(2)} kg
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">
                                Date de livraison
                              </span>
                              <span className="detail-value">
                                {selectedDelivery.date}
                              </span>
                            </div>

                            <div className="detail-item">
                              <span className="detail-label">
                                Camion actuel
                              </span>
                              <span className="detail-value">
                                {selectedDelivery.truck}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="assignment-illustration mt-4">
                        <div className="text-center mb-3">
                          <FontAwesomeIcon
                            icon={faTruck}
                            size="2x"
                            className="text-primary mb-2"
                          />
                          <p className="mb-0 tw-text-xs">
                            Sélectionnez un camion dans la liste
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="detail-item">
                            <span className="detail-label">
                              Nombre de livraisons
                            </span>
                            <span className="detail-value">
                              {selectedDeliveries.length}
                            </span>
                          </div>

                          <div className="detail-item">
                            <span className="detail-label">Poids total</span>
                            <span className="detail-value">
                              {selectedDeliveries
                                .reduce(
                                  (sum, delivery) =>
                                    sum + parseFloat(delivery.weight || 0),
                                  0
                                )
                                .toFixed(2)}{' '}
                              kg
                            </span>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="detail-item">
                            <span className="detail-label">
                              Mètres linéaires totaux
                            </span>
                            <span className="detail-value">
                              {selectedDeliveries
                                .reduce(
                                  (sum, delivery) =>
                                    sum +
                                    parseFloat(delivery.linearMeters || 0),
                                  0
                                )
                                .toFixed(2)}{' '}
                              ml
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="selected-items-list mt-3">
                        <h6 className="modal-subsection-title">
                          Livraisons à réaffecter
                        </h6>
                        <div className="selected-items-container">
                          {selectedDeliveries.map((delivery) => (
                            <div key={delivery.id} className="selected-item">
                              <span className="item-id">#{delivery.id}</span>
                              {delivery.etaTime && (
                                <span
                                  className="item-eta"
                                  style={{
                                    color: '#28a745',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                  }}
                                >
                                  [{delivery.etaTime}]
                                </span>
                              )}
                              <span className="item-client">
                                {delivery.clientName || delivery.client}
                              </span>
                              <span className="item-weight">
                                {delivery.weight.toFixed(2)} kg
                              </span>
                              {delivery.linearMeters > 0 && (
                                <span
                                  className="item-linear-meters"
                                  style={{ fontSize: '11px', color: '#6c757d' }}
                                >
                                  {delivery.linearMeters.toFixed(2)} ml
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="assignment-illustration mt-4">
                        <div className="text-center mb-3">
                          <FontAwesomeIcon
                            icon={faLayerGroup}
                            size="2x"
                            className="text-primary mb-2"
                          />
                          <p className="mb-0 tw-text-xs">
                            Sélectionnez un camion pour ces{' '}
                            {selectedDeliveries.length} livraisons
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Colonne de droite - Liste des camions */}
                <div className="col-md-7">
                  <h5 className="modal-section-title">
                    Sélectionner un camion
                  </h5>
                  <div className="truck-selection">
                    {trucks.map((truck) => {
                      // Calculer le nouveau pourcentage de remplissage en cas d'assignation
                      const existingWeight = truck.usedCapacity;

                      // Si la commande est déjà assignée à ce camion, ne pas ajouter son poids
                      const isAlreadyAssigned =
                        selectedDelivery &&
                        (truck.id === selectedDelivery.vehicleId ||
                          truck.licensePlate === selectedDelivery.truck ||
                          truck.key === selectedDelivery.truck);

                      // Calculer le nouveau poids si cette commande est assignée à ce camion
                      const newWeight = isAlreadyAssigned
                        ? existingWeight
                        : existingWeight +
                          (selectedDelivery
                            ? parseFloat(selectedDelivery.weight) || 0
                            : 0);

                      // Calculer les nouveaux mètres linéaires si cette commande est assignée à ce camion
                      const existingLinearMeters = truck.usedLinearMeters || 0;
                      const newLinearMeters = isAlreadyAssigned
                        ? existingLinearMeters
                        : existingLinearMeters +
                          (selectedDelivery
                            ? parseFloat(selectedDelivery.linearMeters) || 0
                            : 0);

                      // Calculer le pourcentage de remplissage en poids
                      const fillPercentage = Math.min(
                        100,
                        Math.round((newWeight / truck.capacity) * 100)
                      );

                      // Calculer le pourcentage de remplissage en mètres linéaires
                      const linearFillPercentage =
                        truck.linearCapacity > 0
                          ? Math.min(
                              100,
                              Math.round(
                                (newLinearMeters / truck.linearCapacity) * 100
                              )
                            )
                          : 0;

                      // Déterminer la classe de couleur
                      let fillColorClass = 'bg-success';
                      if (fillPercentage > 100) {
                        fillColorClass = 'bg-danger';
                      } else if (fillPercentage > 70) {
                        fillColorClass = 'bg-warning';
                      }

                      // Obtenir la couleur du camion
                      const truckColor = getTruckColor(truck.id);

                      return (
                        <div
                          key={truck.id}
                          className={`truck-option ${selectedTruck && selectedTruck.id === truck.id ? 'selected' : ''}`}
                          onClick={() => handleTruckSelection(truck)}
                        >
                          <span
                            className="truck-color-dot"
                            style={{ backgroundColor: truckColor }}
                          ></span>
                          <div className="truck-option-header">
                            <h6>Camion {truck.key || truck.id}</h6>
                            <span
                              className={`truck-option-capacity ${fillPercentage > 100 ? 'high-load' : ''}`}
                            >
                              {newWeight.toFixed(2)} / {truck.capacity} kg
                            </span>
                            {truck.linearCapacity > 0 && (
                              <span
                                className={`truck-option-capacity ${linearFillPercentage > 100 ? 'high-load' : ''}`}
                                style={{ fontSize: '12px' }}
                              >
                                {newLinearMeters.toFixed(2)} /{' '}
                                {truck.linearCapacity} ml
                              </span>
                            )}
                          </div>
                          <div className="license-info">
                            <small>Immatriculation: {truck.licensePlate}</small>
                          </div>
                          <div
                            className="progress mt-2"
                            style={{
                              marginBottom:
                                truck.linearCapacity > 0 ? '8px' : '0',
                            }}
                          >
                            <div
                              className={`progress-bar ${fillColorClass}`}
                              role="progressbar"
                              style={{ width: `${fillPercentage}%` }}
                              aria-valuenow={fillPercentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                          {truck.linearCapacity > 0 && (
                            <div className="progress mt-1">
                              <div
                                className={`progress-bar ${linearFillPercentage > 100 ? 'bg-danger' : linearFillPercentage > 70 ? 'bg-warning' : 'bg-success'}`}
                                role="progressbar"
                                style={{ width: `${linearFillPercentage}%` }}
                                aria-valuenow={linearFillPercentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          )}
                          {(fillPercentage > 100 ||
                            linearFillPercentage > 100) && (
                            <div className="overload-warning mt-1">
                              <small className="text-danger">
                                Attention: Surcharge
                                {fillPercentage > 100 &&
                                linearFillPercentage > 100
                                  ? ' (poids et mètres linéaires)'
                                  : fillPercentage > 100
                                    ? ' (poids)'
                                    : ' (mètres linéaires)'}
                                !
                              </small>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeAssignModal}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={
                  selectedDelivery ? confirmAssignment : confirmBulkAssignment
                }
                disabled={!selectedTruck}
              >
                Confirmer l'assignation
              </Button>
            </Modal.Footer>
          </Modal>

          {showModalConfig && (
            <ModalConfig
              show={showModalConfig}
              onHide={() => setShowModalConfig(false)}
              BASE_URL={
                process.env.REACT_APP_BASE_URL || 'http://localhost:3001'
              }
            />
          )}
        </div>
      </GenericPageContainer>
    </>
  );
}
